import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ArrowLeft, Camera, User, MapPin, Briefcase, GraduationCap, Heart, Upload, X, Check, Star, Loader2, Image as ImageIcon, AlertCircle, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SelectablePills } from './ui/SelectablePills';
import { useAppDispatch, useAppSelector } from '../store/store';
import { fetchProfile, updateProfile } from '../store/slices/profileSlice';
import { toast } from 'sonner';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { UserAvatar } from './UserAvatar';
import axios from '../utils/axios.config';
import LocationSelector from './LocationSelector';
import { logger } from '../utils/logger';
import { ExtendedProfileFields } from './ExtendedProfileFields';

// Community to Sub-Community mapping
const communitySubCommunityMap: Record<string, string[]> = {
  'Brahmin': ['Anavil', 'Audichya', 'Bhumihar', 'Daivadnya', 'Deshastha', 'Garhwali', 'Gaur', 'Goswami', 'Havyaka', 'Iyer', 'Iyengar', 'Jangid', 'Kanyakubja', 'Karhade', 'Kokanastha', 'Madhwa', 'Maharashtrian', 'Nagar', 'Niyogi', 'Panda', 'Pareek', 'Pushkarna', 'Rigvedi', 'Rudraj', 'Sakaldwipi', 'Saraswat', 'Shivalli', 'Smartha', 'Sri Vaishnava', 'Tyagi', 'Vaidiki', 'Other'],
  'Kshatriya': ['Chandravanshi', 'Gujjar', 'Jat', 'Lodhi', 'Maratha', 'Rajput', 'Reddy', 'Rohilla', 'Saini', 'Suryavanshi', 'Thakur', 'Other'],
  'Vaishya': ['Agarwal', 'Arora', 'Baniya', 'Barnwal', 'Bhandari', 'Chettiar', 'Gupta', 'Jaiswal', 'Khandelwal', 'Komati', 'Mahajan', 'Maheshwari', 'Marwari', 'Modi', 'Oswal', 'Porwal', 'Rastogi', 'Other'],
  'Shudra': ['Kurmi', 'Kunbi', 'Yadav', 'Other'],
  'Scheduled Caste': ['Adi Dravida', 'Chamar', 'Jatav', 'Khatik', 'Mahar', 'Mala', 'Madiga', 'Meghwal', 'Namasudra', 'Pasi', 'Ravidasia', 'Valmiki', 'Other'],
  'Scheduled Tribe': ['Bhil', 'Gond', 'Munda', 'Santhal', 'Oraon', 'Other'],
  'OBC': ['Bishnoi', 'Ezhava', 'Gavli', 'Jangam', 'Kapu', 'Koli', 'Kuruba', 'Lingayat', 'Nadar', 'Naidu', 'Nair', 'Okkaliga', 'Patel', 'Reddy', 'Vokkaliga', 'Other'],
  'Sikh': ['Arora', 'Bhatia', 'Jat', 'Kamboj', 'Khatri', 'Labana', 'Lubana', 'Ramgarhia', 'Ravidas', 'Ravidasia', 'Saini', 'Tonk Kshatriya', 'Other'],
  'Muslim': ['Ansari', 'Arain', 'Awan', 'Bohra', 'Dekkani', 'Dudekula', 'Hanafi', 'Jat', 'Khoja', 'Malik', 'Mapila', 'Memon', 'Mughal', 'Pathan', 'Qureshi', 'Rajput', 'Saifi', 'Shaikh', 'Shafi', 'Shia', 'Siddiqui', 'Sunni', 'Syed', 'Other'],
  'Christian': ['Anglo Indian', 'Born Again', 'Catholic', 'Church of South India (CSI)', 'Jacobite', 'Knanaya', 'Latin Catholic', 'Malankara', 'Marthoma', 'Pentecost', 'Protestant', 'Roman Catholic', 'Syrian Catholic', 'Syrian Orthodox', 'Other'],
  'Jain': ['Digambar', 'Oswal', 'Porwal', 'Shwetambar', 'Other'],
  'Buddhist': ['Ambedkarite', 'Mahayana', 'Theravada', 'Other'],
  'Parsi': ['Irani', 'Zoroastrian', 'Other'],
  'Other': ['No Community', 'Prefer Not to Say']
};

// Personality Tags (100+ options)
const personalityTagsOptions = [
  'Happening', 'Funaholic', 'Caring', 'Chill', 'Adventurous', 'Ambitious', 'Artistic', 'Athletic',
  'Charming', 'Cheerful', 'Compassionate', 'Confident', 'Creative', 'Curious', 'Dependable',
  'Determined', 'Easygoing', 'Empathetic', 'Energetic', 'Enthusiastic', 'Extroverted',
  'Flexible', 'Friendly', 'Funny', 'Generous', 'Gentle', 'Genuine', 'Goal-oriented', 'Gracious',
  'Hardworking', 'Helpful', 'Honest', 'Hopeful', 'Humble', 'Humorous', 'Imaginative',
  'Independent', 'Innovative', 'Intelligent', 'Introverted', 'Intuitive', 'Kind', 'Leader',
  'Loyal', 'Mature', 'Motivated', 'Optimistic', 'Organized', 'Outgoing', 'Passionate',
  'Patient', 'Peaceful', 'Perfectionist', 'Persistent', 'Playful', 'Polite', 'Positive',
  'Practical', 'Professional', 'Quiet', 'Rational', 'Realistic', 'Reflective', 'Reliable',
  'Reserved', 'Resilient', 'Resourceful', 'Respectful', 'Responsible', 'Romantic', 'Self-aware',
  'Selfless', 'Sensitive', 'Serious', 'Shy', 'Sincere', 'Sociable', 'Sophisticated', 'Spontaneous',
  'Stable', 'Strong', 'Supportive', 'Sweet', 'Tactful', 'Thoughtful', 'Tolerant', 'Traditional',
  'Trusting', 'Trustworthy', 'Understanding', 'Unique', 'Versatile', 'Warm', 'Wise', 'Witty',
  'Worldly', 'Zestful', 'Adaptable', 'Affectionate', 'Alert', 'Analytical', 'Articulate',
  'Assertive', 'Attentive', 'Balanced', 'Bold', 'Brave', 'Bright', 'Calm', 'Capable'
];

// Books Genre Options
const booksGenreOptions = [
  'Fantasy', 'Horror', 'Mystery', 'Romance', 'Science Fiction', 'Thriller', 'Biography',
  'Self-Help', 'History', 'Poetry', 'Drama', 'Adventure', 'Crime', 'Philosophy',
  'Travel', 'Comedy', 'Young Adult', 'Classic', 'Contemporary', 'Historical Fiction'
];

// Music Genre Options
const musicGenreOptions = [
  'Pop', 'Rock', 'Hip Hop', 'Jazz', 'Classical', 'Electronic', 'Country', 'R&B',
  'Blues', 'Reggae', 'Folk', 'Metal', 'Indie', 'Soul', 'Funk', 'Latin',
  'Alternative', 'Dance', 'Punk', 'Disco', 'Gospel', 'Techno', 'House', 'Bollywood',
  'Sufi', 'Devotional', 'Ghazal', 'Qawwali', 'Carnatic', 'Hindustani'
];

// Horoscope Signs
const horoscopeSignOptions = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

// Dress Style Options
const dressStyleOptions = [
  'Casual', 'Formal', 'Traditional', 'Trendy', 'Sporty', 'Bohemian', 'Minimalist'
];

// Living With Options
const livingWithOptions = [
  'Parents', 'Alone', 'Relatives', 'Friends', 'Hostel'
];

interface EditProfilePageProductionProps {
  onBack: () => void;
}

interface FormErrors {
  [key: string]: string;
}

interface PhotoItem {
  id: string;
  url: string;
  file?: File;
  isPrimary: boolean;
  isNew: boolean;
  publicId?: string;
  _id?: string; // Database ID for existing photos
  markedForDeletion?: boolean; // Track photos to delete
}

interface FormData {
  gender: string;
  dateOfBirth: string;
  height: string;
  weight: string;
  religion: string;
  community: string;
  subCommunity: string;
  motherTongue: string;
  maritalStatus: string;
  about: string;
  city: string;
  state: string;
  country: string;
  educationLevel: string;
  educationField: string;
  educationInstitution: string;
  occupation: string;
  employmentType: string;
  workLocation: string;
  annualIncome: string;
  alternatePhone: string;
  whatsappNumber: string;
  fatherName: string;
  fatherOccupation: string;
  motherName: string;
  motherOccupation: string;
  brothers: string;
  sisters: string;
  familyType: string;
  familyStatus: string;
  familyValues: string;
  smokingHabits: string;
  drinkingHabits: string;
  foodPreference: string;
  hobbies: string;
  bodyType: string;
  complexion: string;
  disability: string;
  gotra: string;
  manglik: string;
  personalityTags: string[];
  thalassemia: string;
  hivPositive: string;
  familyIncome: string;
  livingWith: string;
  familyBasedOutOf: string;
  horoscopeSign: string;
  ownHouse: string;
  openToPets: string;
  ownCar: string;
  foodICook: string;
  favoriteBooksGenre: string[];
  favoriteMusicGenre: string[];
  dressStyle: string;
  sports: string;
  cuisine: string;
  movies: string;
  tvShows: string;
  vacationDestination: string;
  partnerAgeMin: string;
  partnerAgeMax: string;
  partnerHeightMin: string;
  partnerHeightMax: string;
  partnerMaritalStatus: string;
  partnerReligion: string;
  partnerDiet: string;
  partnerSmoking: string;
  partnerDrinking: string;
}

// Profile field configuration interface
interface ProfileFieldConfig {
  name: string;
  label: string;
  type: string;
  category: string;
  visible: boolean;
  required: boolean;
  order: number;
  editable: boolean;
}

// Helper functions to normalize database values to form values
const normalizeGender = (dbValue: string): string => {
  if (!dbValue) return '';
  return dbValue.charAt(0).toUpperCase() + dbValue.slice(1).toLowerCase();
};

const normalizeMaritalStatus = (dbValue: string): string => {
  if (!dbValue) return '';
  // Convert "never_married" to "Never Married"
  return dbValue
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const normalizeEducationLevel = (dbValue: string): string => {
  if (!dbValue) return '';
  const mapping: { [key: string]: string } = {
    'high school': 'High School',
    'high_school': 'High School',
    'highschool': 'High School',
    'diploma': 'Diploma',
    "bachelor's": "Bachelor's",
    'bachelors': "Bachelor's",
    "master's": "Master's",
    'masters': "Master's",
    'phd': 'PhD',
  };
  return mapping[dbValue.toLowerCase()] || dbValue;
};

// Helper functions to convert form values back to database format
const denormalizeGender = (formValue: string): string => {
  if (!formValue) return '';
  return formValue.toLowerCase();
};

const denormalizeMaritalStatus = (formValue: string): string => {
  if (!formValue) return '';
  // Convert "Never Married" to "never_married"
  return formValue.toLowerCase().replace(/ /g, '_');
};

const denormalizeEducationLevel = (formValue: string): string => {
  if (!formValue) return '';
  const mapping: { [key: string]: string } = {
    'High School': 'high_school',
    'Diploma': 'diploma',
    "Bachelor's": 'bachelors',
    "Master's": 'masters',
    'PhD': 'phd',
  };
  return mapping[formValue] || formValue.toLowerCase();
};

// Helper function to capitalize first letter
const capitalizeFirst = (value: string): string => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

// Normalize religion value to match dropdown options
const normalizeReligion = (dbValue: string): string => {
  if (!dbValue) return '';
  // Capitalize first letter to match dropdown options (Hindu, Muslim, Christian, etc.)
  return capitalizeFirst(dbValue);
};

// Normalize community value to match dropdown options
const normalizeCommunity = (dbValue: string): string => {
  if (!dbValue) return '';
  // Handle special cases and capitalize properly
  const mapping: { [key: string]: string } = {
    'brahmin': 'Brahmin',
    'kshatriya': 'Kshatriya',
    'vaishya': 'Vaishya',
    'shudra': 'Shudra',
    'scheduled caste': 'Scheduled Caste',
    'scheduled tribe': 'Scheduled Tribe',
    'obc': 'OBC',
    'sikh': 'Sikh',
    'muslim': 'Muslim',
    'christian': 'Christian',
    'jain': 'Jain',
    'buddhist': 'Buddhist',
    'parsi': 'Parsi'
  };
  return mapping[dbValue.toLowerCase()] || capitalizeFirst(dbValue);
};

// Normalize select field values to match dropdown options (capitalizes first letter)
const normalizeSelectValue = (dbValue: string): string => {
  if (!dbValue) return '';
  // For values with underscores or hyphens, split and capitalize each word
  if (dbValue.includes('_') || dbValue.includes('-')) {
    return dbValue
      .split(/[_-]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  // For single words, just capitalize first letter
  return capitalizeFirst(dbValue);
};

// Helper functions to normalize employment type from DB to form
const normalizeEmploymentType = (dbValue: string): string => {
  if (!dbValue) return '';
  const mapping: { [key: string]: string } = {
    'self_employed': 'Self-Employed',
    'private': 'Full-Time',
    'government': 'Government',
    'business': 'Business',
    'not_working': 'Unemployed',
  };
  return mapping[dbValue] || dbValue;
};

const denormalizeEmploymentType = (formValue: string): string => {
  if (!formValue) return '';
  const mapping: { [key: string]: string } = {
    'Self-Employed': 'self_employed',
    'Full-Time': 'private',
    'Part-Time': 'private',
    'Government': 'government',
    'Business': 'business',
    'Freelance': 'self_employed',
    'Contract': 'private',
    'Unemployed': 'not_working',
  };
  return mapping[formValue] || formValue.toLowerCase();
};

// Helper functions to normalize smoking habits from DB to form
const normalizeSmokingHabits = (dbValue: string): string => {
  if (!dbValue) return '';
  const mapping: { [key: string]: string } = {
    'no': 'Non-Smoker',
    'occasionally': 'Occasional Smoker',
    'yes': 'Regular Smoker',
  };
  return mapping[dbValue] || dbValue;
};

const denormalizeSmokingHabits = (formValue: string): string => {
  if (!formValue) return '';
  const mapping: { [key: string]: string } = {
    'Non-Smoker': 'no',
    'Occasional Smoker': 'occasionally',
    'Regular Smoker': 'yes',
  };
  return mapping[formValue] || formValue.toLowerCase();
};

// Helper functions to normalize drinking habits from DB to form
const normalizeDrinkingHabits = (dbValue: string): string => {
  if (!dbValue) return '';
  const mapping: { [key: string]: string } = {
    'no': 'Non-Drinker',
    'occasionally': 'Social Drinker',
    'yes': 'Regular Drinker',
  };
  return mapping[dbValue] || dbValue;
};

const denormalizeDrinkingHabits = (formValue: string): string => {
  if (!formValue) return '';
  const mapping: { [key: string]: string } = {
    'Non-Drinker': 'no',
    'Social Drinker': 'occasionally',
    'Regular Drinker': 'yes',
  };
  return mapping[formValue] || formValue.toLowerCase();
};

// Helper functions to normalize food preference from DB to form
const normalizeFoodPreference = (dbValue: string): string => {
  if (!dbValue) return '';
  const mapping: { [key: string]: string } = {
    'vegetarian': 'Vegetarian',
    'non_vegetarian': 'Non-Vegetarian',
    'eggetarian': 'Eggetarian',
    'vegan': 'Vegan',
  };
  return mapping[dbValue] || dbValue;
};

const denormalizeFoodPreference = (formValue: string): string => {
  if (!formValue) return '';
  const mapping: { [key: string]: string } = {
    'Vegetarian': 'vegetarian',
    'Non-Vegetarian': 'non_vegetarian',
    'Eggetarian': 'eggetarian',
    'Vegan': 'vegan',
  };
  return mapping[formValue] || formValue.toLowerCase();
};

// Helper functions for family fields
const denormalizeFamilyType = (formValue: string): string => {
  if (!formValue) return '';
  const mapping: { [key: string]: string } = {
    'Joint': 'joint',
    'Nuclear': 'nuclear',
    'Joint Family': 'joint',
    'Nuclear Family': 'nuclear',
  };
  return mapping[formValue] || formValue.toLowerCase().replace(/\s+/g, '_');
};

const denormalizeFamilyStatus = (formValue: string): string => {
  if (!formValue) return '';
  const mapping: { [key: string]: string } = {
    'Middle Class': 'middle_class',
    'Upper Middle Class': 'upper_middle_class',
    'Rich': 'rich',
    'Affluent': 'affluent',
  };
  return mapping[formValue] || formValue.toLowerCase().replace(/\s+/g, '_');
};

const denormalizeFamilyValues = (formValue: string): string => {
  if (!formValue) return '';
  const mapping: { [key: string]: string } = {
    'Traditional': 'traditional',
    'Moderate': 'moderate',
    'Liberal': 'liberal',
  };
  return mapping[formValue] || formValue.toLowerCase();
};

export default function EditProfilePageProduction({ onBack }: EditProfilePageProductionProps) {
  const dispatch = useAppDispatch();
  const { data: profile, loading, saving } = useAppSelector(state => state.profile);
  const { user } = useAppSelector(state => state.auth);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [formData, setFormData] = useState<FormData>({
    gender: '',
    dateOfBirth: '',
    height: '',
    weight: '',
    religion: '',
    community: '',
    caste: '',
    motherTongue: '',
    maritalStatus: '',
    about: '',
    city: '',
    state: '',
    country: 'India',
    educationLevel: '',
    educationField: '',
    educationInstitution: '',
    occupation: '',
    employmentType: '',
    workLocation: '',
    annualIncome: '',
    alternatePhone: '',
    whatsappNumber: '',
    fatherName: '',
    fatherOccupation: '',
    motherName: '',
    motherOccupation: '',
    brothers: '',
    sisters: '',
    familyType: '',
    familyStatus: '',
    familyValues: '',
    smokingHabits: '',
    drinkingHabits: '',
    foodPreference: '',
    hobbies: '',
    bodyType: '',
    complexion: '',
    disability: '',
    gotra: '',
    manglik: '',
    personalityTags: [],
    thalassemia: '',
    hivPositive: '',
    familyIncome: '',
    livingWith: '',
    familyBasedOutOf: '',
    horoscopeSign: '',
    ownHouse: '',
    openToPets: '',
    ownCar: '',
    foodICook: '',
    favoriteBooksGenre: [],
    favoriteMusicGenre: [],
    dressStyle: '',
    sports: '',
    cuisine: '',
    movies: '',
    tvShows: '',
    vacationDestination: '',
    partnerAgeMin: '',
    partnerAgeMax: '',
    partnerHeightMin: '',
    partnerHeightMax: '',
    partnerMaritalStatus: '',
    partnerReligion: '',
    partnerDiet: '',
    partnerSmoking: '',
    partnerDrinking: ''
  });

  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  // Profile fields configuration
  const [fieldConfigs, setFieldConfigs] = useState<ProfileFieldConfig[]>([]);
  const [configLoading, setConfigLoading] = useState(true);

  // Default field configurations (used when API is unavailable)
  const defaultFieldConfigs: ProfileFieldConfig[] = [
    // Basic Information
    { name: 'gender', label: 'Gender', type: 'select', category: 'Basic Information', visible: true, required: true, order: 1, editable: false },
    { name: 'dateOfBirth', label: 'Date of Birth', type: 'date', category: 'Basic Information', visible: true, required: true, order: 2, editable: true },
    { name: 'height', label: 'Height (feet\'inches")', type: 'text', category: 'Basic Information', visible: true, required: false, order: 3, editable: true },
    { name: 'weight', label: 'Weight (kg)', type: 'number', category: 'Basic Information', visible: true, required: false, order: 4, editable: true },
    { name: 'maritalStatus', label: 'Marital Status', type: 'select', category: 'Basic Information', visible: true, required: true, order: 5, editable: true },
    { name: 'about', label: 'About Me', type: 'textarea', category: 'Basic Information', visible: true, required: false, order: 6, editable: true },

    // Religious Background
    { name: 'religion', label: 'Religion', type: 'select', category: 'Religious Background', visible: true, required: true, order: 7, editable: true },
    { name: 'community', label: 'Community', type: 'select', category: 'Religious Background', visible: true, required: false, order: 8, editable: true },
    { name: 'subCommunity', label: 'Sub Community', type: 'select', category: 'Religious Background', visible: true, required: false, order: 9, editable: true },
    { name: 'motherTongue', label: 'Mother Tongue', type: 'text', category: 'Religious Background', visible: true, required: false, order: 10, editable: true },

    // Location
    { name: 'country', label: 'Country', type: 'select', category: 'Location', visible: true, required: true, order: 11, editable: true },
    { name: 'state', label: 'State', type: 'select', category: 'Location', visible: true, required: true, order: 12, editable: true },
    { name: 'city', label: 'City', type: 'select', category: 'Location', visible: true, required: true, order: 13, editable: true },

    // Education
    { name: 'educationLevel', label: 'Highest Qualification', type: 'select', category: 'Education', visible: true, required: true, order: 14, editable: true },
    { name: 'educationField', label: 'Field of Study', type: 'text', category: 'Education', visible: true, required: false, order: 15, editable: true },
    { name: 'educationInstitution', label: 'Institution', type: 'text', category: 'Education', visible: true, required: false, order: 16, editable: true },

    // Career
    { name: 'occupation', label: 'Occupation', type: 'text', category: 'Career', visible: true, required: true, order: 17, editable: true },
    { name: 'employmentType', label: 'Employment Type', type: 'select', category: 'Career', visible: true, required: false, order: 18, editable: true },
    { name: 'workLocation', label: 'Work Location', type: 'text', category: 'Career', visible: true, required: false, order: 19, editable: true },
    { name: 'annualIncome', label: 'Annual Income', type: 'select', category: 'Career', visible: true, required: false, order: 20, editable: true },

    // Contact
    { name: 'alternatePhone', label: 'Alternate Phone', type: 'text', category: 'Contact', visible: true, required: false, order: 21, editable: true },
    { name: 'whatsappNumber', label: 'WhatsApp Number', type: 'text', category: 'Contact', visible: true, required: false, order: 22, editable: true },

    // Family
    { name: 'fatherName', label: "Father's Name", type: 'text', category: 'Family', visible: true, required: false, order: 23, editable: true },
    { name: 'fatherOccupation', label: "Father's Occupation", type: 'text', category: 'Family', visible: true, required: false, order: 24, editable: true },
    { name: 'motherName', label: "Mother's Name", type: 'text', category: 'Family', visible: true, required: false, order: 25, editable: true },
    { name: 'motherOccupation', label: "Mother's Occupation", type: 'text', category: 'Family', visible: true, required: false, order: 26, editable: true },
    { name: 'brothers', label: 'Number of Brothers', type: 'number', category: 'Family', visible: true, required: false, order: 27, editable: true },
    { name: 'sisters', label: 'Number of Sisters', type: 'number', category: 'Family', visible: true, required: false, order: 28, editable: true },
    { name: 'familyType', label: 'Family Type', type: 'select', category: 'Family', visible: true, required: false, order: 29, editable: true },
    { name: 'familyStatus', label: 'Family Status', type: 'select', category: 'Family', visible: true, required: false, order: 30, editable: true },
    { name: 'familyValues', label: 'Family Values', type: 'select', category: 'Family', visible: true, required: false, order: 31, editable: true },

    // Lifestyle
    { name: 'smokingHabits', label: 'Smoking Habits', type: 'select', category: 'Lifestyle', visible: true, required: false, order: 32, editable: true },
    { name: 'drinkingHabits', label: 'Drinking Habits', type: 'select', category: 'Lifestyle', visible: true, required: false, order: 33, editable: true },
    { name: 'foodPreference', label: 'Food Preference', type: 'select', category: 'Lifestyle', visible: true, required: false, order: 34, editable: true },
    { name: 'hobbies', label: 'Hobbies', type: 'textarea', category: 'Lifestyle', visible: true, required: false, order: 35, editable: true },

    // Physical
    { name: 'bodyType', label: 'Body Type', type: 'select', category: 'Physical', visible: true, required: false, order: 36, editable: true },
    { name: 'complexion', label: 'Complexion', type: 'select', category: 'Physical', visible: true, required: false, order: 37, editable: true },
    { name: 'disability', label: 'Physical Disability', type: 'text', category: 'Physical', visible: true, required: false, order: 38, editable: true },

    // Astrology
    { name: 'gotra', label: 'Gotra', type: 'text', category: 'Astrology', visible: true, required: false, order: 39, editable: true },
    { name: 'manglik', label: 'Manglik Status', type: 'select', category: 'Astrology', visible: true, required: false, order: 40, editable: true },

    // Partner Preferences
    { name: 'partnerPreferences.ageRange.min', label: 'Partner Age From', type: 'number', category: 'Partner Preferences', visible: true, required: false, order: 41, editable: true },
    { name: 'partnerPreferences.ageRange.max', label: 'Partner Age To', type: 'number', category: 'Partner Preferences', visible: true, required: false, order: 42, editable: true },
    { name: 'partnerPreferences.heightRange.min', label: 'Partner Height From (feet\'inches")', type: 'text', category: 'Partner Preferences', visible: true, required: false, order: 43, editable: true },
    { name: 'partnerPreferences.heightRange.max', label: 'Partner Height To (feet\'inches")', type: 'text', category: 'Partner Preferences', visible: true, required: false, order: 44, editable: true },
    { name: 'partnerPreferences.maritalStatus', label: 'Partner Marital Status', type: 'select', category: 'Partner Preferences', visible: true, required: false, order: 45, editable: true },
    { name: 'partnerPreferences.religion', label: 'Partner Religion', type: 'select', category: 'Partner Preferences', visible: true, required: false, order: 46, editable: true },
    { name: 'partnerPreferences.diet', label: 'Partner Food Preference', type: 'select', category: 'Partner Preferences', visible: true, required: false, order: 47, editable: true },
    { name: 'partnerPreferences.smoking', label: 'Partner Smoking Habits', type: 'select', category: 'Partner Preferences', visible: true, required: false, order: 48, editable: true },
    { name: 'partnerPreferences.drinking', label: 'Partner Drinking Habits', type: 'select', category: 'Partner Preferences', visible: true, required: false, order: 49, editable: true },
  ];

  // Fetch profile field configurations
  useEffect(() => {
    const fetchFieldConfigs = async () => {
      try {
        setConfigLoading(true);
        const response = await axios.get('/profiles/fields-config');
        if (response.data.success) {
          const configs = response.data.data.fields || [];
          setFieldConfigs(configs);
          logger.log('📋 Loaded field configurations from API:', configs.length);
        }
      } catch (error) {
        logger.log('⚠️ Could not fetch field configs from API, using defaults');
        // Use default configs if fetch fails (e.g., in demo mode or backend unavailable)
        setFieldConfigs(defaultFieldConfigs);
      } finally {
        setConfigLoading(false);
      }
    };

    fetchFieldConfigs();
  }, []);

  // Helper function to check if a field is visible
  const isFieldVisible = (fieldName: string): boolean => {
    if (configLoading || fieldConfigs.length === 0) return true; // Show all by default
    const config = fieldConfigs.find(f => f.name === fieldName);
    return config ? config.visible : true;
  };

  // Helper function to check if a field is required
  const isFieldRequired = (fieldName: string): boolean => {
    if (configLoading || fieldConfigs.length === 0) {
      // Fallback to hardcoded required fields
      const staticRequiredFields = ['gender', 'dateOfBirth', 'maritalStatus', 'religion', 'city', 'state', 'country', 'educationLevel', 'occupation'];
      return staticRequiredFields.includes(fieldName);
    }
    const config = fieldConfigs.find(f => f.name === fieldName);
    return config ? config.required : false;
  };

  // Static fallback required fields
  const staticRequiredFields: { [K in keyof FormData]?: string } = {
    gender: 'Gender is required',
    dateOfBirth: 'Date of Birth is required',
    maritalStatus: 'Marital Status is required',
    religion: 'Religion is required',
    city: 'City is required',
    state: 'State is required',
    country: 'Country is required',
    educationLevel: 'Highest Qualification is required',
    occupation: 'Occupation is required'
  };

  // Build dynamic required fields from config using useMemo
  const requiredFields = useMemo<{ [K in keyof FormData]?: string }>(() => {
    if (configLoading || fieldConfigs.length === 0) {
      return staticRequiredFields;
    }

    const dynamicRequired: { [key: string]: string } = {};
    fieldConfigs.forEach(config => {
      if (config.required && config.visible) {
        dynamicRequired[config.name] = `${config.label} is required`;
      }
    });

    return dynamicRequired as { [K in keyof FormData]?: string };
  }, [configLoading, fieldConfigs]);

  // Load profile data on mount - ALWAYS fetch fresh data
  useEffect(() => {
    logger.log('🔄 Component mounted, fetching fresh profile data...');
    setInitialLoadDone(false);
    dispatch(fetchProfile());
  }, []); // Only run on mount, no dependencies

  // Populate form when profile data is loaded
  useEffect(() => {
    if (profile && !initialLoadDone) {
      logger.log('📋 Populating form with profile data:', profile);
      logger.log('📍 Location data from backend:', {
        country: profile.country,
        state: profile.state,
        city: profile.city
      });

      // Populate form fields with normalized values
      const newFormData: FormData = {
        gender: normalizeGender(profile.gender || ''),
        dateOfBirth: profile.dateOfBirth
          ? new Date(profile.dateOfBirth).toISOString().split('T')[0]
          : '',
        height: profile.height ? (() => {
          // Convert from cm to feet'inches" format
          const heightInCm = parseInt(String(profile.height));
          const totalInches = Math.round(heightInCm / 2.54); // Convert cm to inches
          const feet = Math.floor(totalInches / 12);
          const inches = totalInches % 12;
          return `${feet}'${inches}"`;
        })() : '',
        weight: profile.weight?.toString() || '',
        religion: normalizeReligion(Array.isArray(profile.religion) ? profile.religion.join(', ') : (profile.religion || '')),
        community: normalizeCommunity(Array.isArray(profile.community) ? profile.community.join(', ') : (profile.community || '')),
        subCommunity: normalizeSelectValue(Array.isArray(profile.subCommunity)
          ? profile.subCommunity.join(', ')
          : Array.isArray(profile.caste)
            ? profile.caste.join(', ')
            : (profile.subCommunity || profile.caste || '')),
        motherTongue: normalizeSelectValue(Array.isArray(profile.motherTongue) ? profile.motherTongue.join(', ') : (profile.motherTongue || '')),
        maritalStatus: normalizeMaritalStatus(
          Array.isArray(profile.maritalStatus) ? profile.maritalStatus.join(', ') : (profile.maritalStatus || '')
        ),
        about: profile.about || profile.bio || '',
        city: profile.city || '',
        state: profile.state || '',
        country: profile.country || 'India',
        educationLevel: normalizeEducationLevel(profile.education?.level || ''),
        educationField: profile.education?.field || '',
        educationInstitution: profile.education?.institution || '',
        occupation: profile.occupation || '',
        employmentType: normalizeEmploymentType(profile.employmentType || ''),
        workLocation: profile.workLocation || '',
        annualIncome: profile.annualIncome?.toString() || '',
        alternatePhone: profile.alternatePhone || '',
        whatsappNumber: profile.whatsappNumber || '',
        fatherName: profile.family?.fatherName || '',
        fatherOccupation: profile.family?.fatherOccupation || '',
        motherName: profile.family?.motherName || '',
        motherOccupation: profile.family?.motherOccupation || '',
        brothers: profile.family?.siblings?.brothers?.toString() || '',
        sisters: profile.family?.siblings?.sisters?.toString() || '',
        familyType: normalizeSelectValue(profile.family?.familyType || ''),
        familyStatus: normalizeSelectValue(profile.family?.familyStatus || ''),
        familyValues: normalizeSelectValue(profile.family?.familyValues || ''),
        smokingHabits: normalizeSmokingHabits(profile.smoking || ''),
        drinkingHabits: normalizeDrinkingHabits(profile.drinking || ''),
        foodPreference: normalizeFoodPreference(profile.diet || ''),
        hobbies: Array.isArray(profile.hobbies) ? profile.hobbies.join(', ') : (profile.hobbies || ''),
        bodyType: capitalizeFirst(profile.bodyType || ''),
        complexion: normalizeSelectValue(profile.complexion || ''),
        disability: profile.disability || '',
        gotra: profile.gothra || '', // Note: model uses 'gothra' not 'gotra'
        manglik: normalizeSelectValue(profile.horoscope?.manglik || ''),
        personalityTags: Array.isArray(profile.personalityTags) ? profile.personalityTags : [],
        thalassemia: normalizeSelectValue(profile.thalassemia || ''),
        hivPositive: normalizeSelectValue(profile.hivPositive || ''),
        familyIncome: profile.family?.familyIncome || '',
        livingWith: capitalizeFirst(profile.family?.livingWith || ''),
        familyBasedOutOf: profile.family?.familyBasedOutOf || '',
        horoscopeSign: capitalizeFirst(profile.horoscopeSign || ''),
        ownHouse: normalizeSelectValue(profile.ownHouse || ''),
        openToPets: normalizeSelectValue(profile.openToPets || ''),
        ownCar: normalizeSelectValue(profile.ownCar || ''),
        foodICook: profile.foodICook || '',
        favoriteBooksGenre: Array.isArray(profile.favoriteBooksGenre) ? profile.favoriteBooksGenre : [],
        favoriteMusicGenre: Array.isArray(profile.favoriteMusicGenre) ? profile.favoriteMusicGenre : [],
        dressStyle: capitalizeFirst(profile.dressStyle || ''),
        sports: profile.sports || '',
        cuisine: profile.cuisine || '',
        movies: profile.movies || '',
        tvShows: profile.tvShows || '',
        vacationDestination: profile.vacationDestination || '',
        partnerAgeMin: profile.partnerPreferences?.ageRange?.min?.toString() || '',
        partnerAgeMax: profile.partnerPreferences?.ageRange?.max?.toString() || '',
        partnerHeightMin: profile.partnerPreferences?.heightRange?.min ? (() => {
          const heightInCm = parseInt(String(profile.partnerPreferences.heightRange.min));
          if (isNaN(heightInCm)) return profile.partnerPreferences.heightRange.min;
          const totalInches = Math.round(heightInCm / 2.54); // Convert cm to inches
          const feet = Math.floor(totalInches / 12);
          const inches = totalInches % 12;
          return `${feet}'${inches}"`;
        })() : '',
        partnerHeightMax: profile.partnerPreferences?.heightRange?.max ? (() => {
          const heightInCm = parseInt(String(profile.partnerPreferences.heightRange.max));
          if (isNaN(heightInCm)) return profile.partnerPreferences.heightRange.max;
          const totalInches = Math.round(heightInCm / 2.54); // Convert cm to inches
          const feet = Math.floor(totalInches / 12);
          const inches = totalInches % 12;
          return `${feet}'${inches}"`;
        })() : '',
        partnerMaritalStatus: Array.isArray(profile.partnerPreferences?.maritalStatus)
          ? profile.partnerPreferences.maritalStatus.join(', ')
          : (profile.partnerPreferences?.maritalStatus || ''),
        partnerReligion: Array.isArray(profile.partnerPreferences?.religion)
          ? profile.partnerPreferences.religion.join(', ')
          : (profile.partnerPreferences?.religion || ''),
        partnerDiet: normalizeFoodPreference(
          Array.isArray(profile.partnerPreferences?.diet)
            ? profile.partnerPreferences.diet.join(', ')
            : (profile.partnerPreferences?.diet || '')
        ),
        partnerSmoking: normalizeSmokingHabits(
          Array.isArray(profile.partnerPreferences?.smoking)
            ? profile.partnerPreferences.smoking.join(', ')
            : (profile.partnerPreferences?.smoking || '')
        ),
        partnerDrinking: normalizeDrinkingHabits(
          Array.isArray(profile.partnerPreferences?.drinking)
            ? profile.partnerPreferences.drinking.join(', ')
            : (profile.partnerPreferences?.drinking || '')
        )
      };

      logger.log('✅ Normalized form data:', newFormData);
      logger.log('📍 Location in form state:', {
        country: newFormData.country,
        state: newFormData.state,
        city: newFormData.city
      });
      setFormData(newFormData);

      // Check if user has pending changes
      if ((profile as any).hasPendingChanges) {
        setHasPendingChanges(true);
      }

      // Load existing photos - ONLY from profile.photos array
      if (profile.photos && Array.isArray(profile.photos) && profile.photos.length > 0) {
        const existingPhotos: PhotoItem[] = profile.photos.map((photo: any, index: number) => ({
          id: photo._id || `existing-${index}`,
          _id: photo._id, // Store database ID for deletion
          url: photo.url,
          isPrimary: photo.isPrimary || false,
          isNew: false,
          isApproved: photo.isApproved !== undefined ? photo.isApproved : true, // Map isApproved
          publicId: photo.publicId,
          markedForDeletion: false
        }));
        setPhotos(existingPhotos);
        logger.log('📸 Loaded existing photos:', existingPhotos.length);
      } else if (profile.profilePhoto) {
        // Fallback to single profile photo
        setPhotos([{
          id: 'profile-photo',
          url: profile.profilePhoto,
          isPrimary: true,
          isNew: false,
          isApproved: true, // Assume true for legacy single photo
          markedForDeletion: false
        }]);
        logger.log('📸 Loaded single profile photo');
      } else {
        // No photos at all
        setPhotos([]);
        logger.log('📸 No existing photos');
      }

      setInitialLoadDone(true);
    }
  }, [profile, initialLoadDone]);

  // Validate entire form
  const validateForm = (): boolean => {
    logger.log('🔍 Validating form...');
    const newErrors: FormErrors = {};

    // Check all required fields
    Object.keys(requiredFields).forEach((field) => {
      const fieldKey = field as keyof FormData;
      const value = formData[fieldKey];

      // Skip validation for dateOfBirth if it already exists and wasn't changed
      if (fieldKey === 'dateOfBirth' && profile?.dateOfBirth && formData.dateOfBirth) {
        // Date already exists, skip required validation
        return;
      }

      if (!value || value.toString().trim() === '') {
        newErrors[field] = requiredFields[fieldKey]!;
      }
    });

    // Custom validations
    if (formData.dateOfBirth) {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const dayDiff = today.getDate() - birthDate.getDate();

      // Adjust age if birthday hasn't occurred this year
      const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;

      if (actualAge < 18) {
        newErrors.dateOfBirth = 'You must be at least 18 years old';
      }
    }

    if (formData.height && formData.height.trim() !== '') {
      const heightRegex = /^\d{1,2}'(\d{1,2})"$/;
      if (!heightRegex.test(formData.height)) {
        newErrors.height = 'Height must be in format: feet\'inches" (e.g., 5\'6" or 6\'7")';
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      logger.log('❌ Validation failed:', newErrors);
      return false;
    }

    logger.log('✅ Validation passed');
    return true;
  };

  // Handle field blur for individual validation
  const handleBlur = (field: keyof FormData) => {
    setTouched(prev => ({ ...prev, [field]: true }));

    // Validate single field
    const newErrors = { ...errors };
    const value = formData[field];

    if (requiredFields[field] && (!value || value.toString().trim() === '')) {
      newErrors[field] = requiredFields[field]!;
    } else {
      delete newErrors[field];
    }

    // Custom validations for specific fields
    if (field === 'dateOfBirth' && value) {
      const birthDate = new Date(value);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const dayDiff = today.getDate() - birthDate.getDate();
      const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;

      if (actualAge < 18) {
        newErrors.dateOfBirth = 'You must be at least 18 years old';
      } else {
        delete newErrors.dateOfBirth;
      }
    }

    if (field === 'height' && value && value.trim() !== '') {
      const heightRegex = /^\d{1,2}'(\d{1,2})"$/;
      if (!heightRegex.test(value)) {
        newErrors.height = 'Height must be in format: feet\'inches" (e.g., 5\'6" or 6\'7")';
      } else {
        delete newErrors.height;
      }
    }

    setErrors(newErrors);
  };

  // Handle form field changes
  const handleFieldChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  // Convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Handle multiple image selection
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    if (files.length === 0) return;

    // Count only non-deleted photos
    const activePhotos = photos.filter(p => !p.markedForDeletion);

    // Check total photo limit - Maximum 6 photos
    if (activePhotos.length + files.length > 6) {
      toast.error(`Maximum 6 photos allowed. You currently have ${activePhotos.length} photos.`);
      return;
    }

    // Validate each file
    const validFiles: File[] = [];
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum size is 5MB.`);
        continue;
      }

      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not a valid image file.`);
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    // Create preview URLs for new photos
    const newPhotos: PhotoItem[] = validFiles.map((file, index) => ({
      id: `new-${Date.now()}-${index}`,
      url: URL.createObjectURL(file),
      file,
      isPrimary: activePhotos.length === 0 && index === 0,
      isNew: true,
      markedForDeletion: false
    }));

    setPhotos(prev => [...prev, ...newPhotos]);
    toast.success(`${validFiles.length} photo(s) added`);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove photo - mark for deletion or remove from state
  const handleRemovePhoto = async (photoId: string) => {
    const photoToRemove = photos.find(p => p.id === photoId);

    if (!photoToRemove) return;

    // If it's a new photo, just remove from state
    if (photoToRemove.isNew) {
      setPhotos(prevPhotos => {
        const filtered = prevPhotos.filter(p => p.id !== photoId);

        // If removing primary photo, set first remaining photo as primary
        if (photoToRemove.isPrimary && filtered.length > 0) {
          const firstNonDeleted = filtered.find(p => !p.markedForDeletion);
          if (firstNonDeleted) {
            firstNonDeleted.isPrimary = true;
          }
        }

        return filtered;
      });

      // Revoke object URL for new photos
      if (photoToRemove.url.startsWith('blob:')) {
        URL.revokeObjectURL(photoToRemove.url);
      }

      toast.info('Photo removed');
      return;
    }

    // For existing photos, delete from backend immediately
    if (photoToRemove._id) {
      try {
        setUploading(true);
        const { data } = await axios.delete(`/profiles/photos/${photoToRemove._id}/delete`);

        if (data.success) {
          // Remove from state after successful deletion
          setPhotos(prevPhotos => {
            const filtered = prevPhotos.filter(p => p.id !== photoId);

            // If removing primary photo, set first remaining photo as primary
            if (photoToRemove.isPrimary && filtered.length > 0) {
              const firstNonDeleted = filtered.find(p => !p.markedForDeletion);
              if (firstNonDeleted) {
                firstNonDeleted.isPrimary = true;
              }
            }

            return filtered;
          });

          toast.success('Photo deleted successfully');
        }
      } catch (error: any) {
        logger.error('❌ Delete photo error:', error);
        toast.error('Failed to delete photo: ' + (error.response?.data?.message || error.message));
      } finally {
        setUploading(false);
      }
    }
  };

  // Set primary photo
  const handleSetPrimary = (photoId: string) => {
    setPhotos(prevPhotos =>
      prevPhotos.map(p => ({
        ...p,
        isPrimary: p.id === photoId && !p.markedForDeletion
      }))
    );
    toast.success('Primary photo updated');
  };

  // Handle save
  const handleSave = async () => {
    logger.log('💾 Save button clicked');

    // Prevent multiple simultaneous submissions
    if (isSubmitting || saving || uploading) {
      logger.log('⚠️ Already submitting, ignoring duplicate click');
      return;
    }

    setIsSubmitting(true);

    // Mark all required fields as touched
    const allTouched: { [key: string]: boolean } = {};
    Object.keys(requiredFields).forEach(field => {
      allTouched[field] = true;
    });
    setTouched(allTouched);

    // Validate form
    const isValid = validateForm();

    if (!isValid) {
      setIsSubmitting(false);
      toast.error('Please fill all required fields marked with *', {
        duration: 4000,
        icon: <AlertCircle className="size-5 text-red-500" />
      });

      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const element = document.getElementById(firstErrorField);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
        }
      }

      return;
    }

    try {
      setUploading(true);

      // Step 1: Upload new photos if any
      const newPhotos = photos.filter(p => p.isNew && p.file && !p.markedForDeletion);
      let uploadedPhotoUrls: string[] = [];

      if (newPhotos.length > 0) {
        logger.log(`📸 Uploading ${newPhotos.length} new photo(s)...`);
        toast.info(`Uploading ${newPhotos.length} photo(s)...`, { duration: 2000 });

        try {
          // Convert files to base64
          const base64Images = await Promise.all(
            newPhotos.map(photo => fileToBase64(photo.file!))
          );

          // Find primary photo index in new photos
          const primaryPhotoInNew = newPhotos.findIndex(p => p.isPrimary);

          // Upload to backend
          const { data } = await axios.post('/profiles/upload-multiple-photos', {
            images: base64Images,
            primaryIndex: primaryPhotoInNew >= 0 ? primaryPhotoInNew : 0
          });

          if (data.success && data.data.uploadedPhotos) {
            logger.log('✅ Photos uploaded:', data.data.totalUploaded);
            uploadedPhotoUrls = data.data.uploadedPhotos.map((p: any) => p.url);
            toast.success(`${data.data.totalUploaded} photo(s) uploaded`);
          }
        } catch (uploadError: any) {
          logger.error('❌ Photo upload error:', uploadError);
          toast.error('Failed to upload photos. Continuing with profile save...');
        }
      }

      setUploading(false);

      // Step 2: Determine primary photo URL from remaining photos
      let primaryPhotoUrl: string | undefined;

      // Get all active photos (not deleted, including new ones)
      const activePhotos = photos.filter(p => !p.markedForDeletion);
      const primaryPhoto = activePhotos.find(p => p.isPrimary);

      if (primaryPhoto) {
        if (primaryPhoto.isNew) {
          // Use newly uploaded photo
          const photoIndex = newPhotos.findIndex(p => p.id === primaryPhoto.id);
          primaryPhotoUrl = uploadedPhotoUrls[photoIndex] || uploadedPhotoUrls[0];
        } else {
          // Use existing photo
          primaryPhotoUrl = primaryPhoto.url;
        }
      }

      // Step 3: Prepare profile data with denormalized values
      const profileData: any = {
        gender: denormalizeGender(formData.gender.trim()),
        dateOfBirth: formData.dateOfBirth,
        maritalStatus: denormalizeMaritalStatus(formData.maritalStatus.trim()),
        religion: formData.religion.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        country: formData.country.trim(),
        education: {
          level: denormalizeEducationLevel(formData.educationLevel.trim()),
          field: formData.educationField.trim() || undefined,
          institution: formData.educationInstitution.trim() || undefined
        },
        occupation: formData.occupation.trim()
      };

      // Add optional Basic Information fields
      if (formData.height && formData.height.trim() !== '') {
        // Convert height from feet'inches" format to total inches
        const heightMatch = formData.height.trim().match(/^(\d{1,2})'(\d{1,2})"$/);
        if (heightMatch) {
          const feet = parseInt(heightMatch[1]);
          const inches = parseInt(heightMatch[2]);
          const totalInches = feet * 12 + inches;
          profileData.height = Math.round(totalInches * 2.54); // Convert inches to cm
        }
      }

      if (formData.weight && formData.weight.trim() !== '') {
        profileData.weight = parseInt(formData.weight);
      }

      if (formData.community && formData.community.trim() !== '') {
        profileData.community = formData.community.trim();
      }

      if (formData.subCommunity && formData.subCommunity.trim() !== '') {
        profileData.subCommunity = formData.subCommunity.trim();
      }

      if (formData.motherTongue && formData.motherTongue.trim() !== '') {
        profileData.motherTongue = formData.motherTongue.trim();
      }

      if (formData.about && formData.about.trim() !== '') {
        profileData.about = formData.about.trim();
      }

      // Add Career fields
      if (formData.workLocation && formData.workLocation.trim() !== '') {
        profileData.workLocation = formData.workLocation.trim();
      }

      if (formData.employmentType && formData.employmentType.trim() !== '') {
        profileData.employmentType = denormalizeEmploymentType(formData.employmentType.trim());
      }

      if (formData.annualIncome && formData.annualIncome.trim() !== '') {
        profileData.annualIncome = formData.annualIncome.trim();
      }

      // Add Contact fields
      if (formData.alternatePhone && formData.alternatePhone.trim() !== '') {
        profileData.alternatePhone = formData.alternatePhone.trim();
      }

      if (formData.whatsappNumber && formData.whatsappNumber.trim() !== '') {
        profileData.whatsappNumber = formData.whatsappNumber.trim();
      }

      // Add Family fields
      const familyData: any = {};
      if (formData.fatherName && formData.fatherName.trim() !== '') {
        familyData.fatherName = formData.fatherName.trim();
      }
      if (formData.fatherOccupation && formData.fatherOccupation.trim() !== '') {
        familyData.fatherOccupation = formData.fatherOccupation.trim();
      }
      if (formData.motherName && formData.motherName.trim() !== '') {
        familyData.motherName = formData.motherName.trim();
      }
      if (formData.motherOccupation && formData.motherOccupation.trim() !== '') {
        familyData.motherOccupation = formData.motherOccupation.trim();
      }
      if (formData.brothers && formData.brothers.trim() !== '') {
        familyData.siblings = familyData.siblings || {};
        familyData.siblings.brothers = parseInt(formData.brothers);
      }
      if (formData.sisters && formData.sisters.trim() !== '') {
        familyData.siblings = familyData.siblings || {};
        familyData.siblings.sisters = parseInt(formData.sisters);
      }
      if (formData.familyType && formData.familyType.trim() !== '') {
        familyData.familyType = denormalizeFamilyType(formData.familyType.trim());
      }
      if (formData.familyStatus && formData.familyStatus.trim() !== '') {
        familyData.familyStatus = denormalizeFamilyStatus(formData.familyStatus.trim());
      }
      if (formData.familyValues && formData.familyValues.trim() !== '') {
        familyData.familyValues = denormalizeFamilyValues(formData.familyValues.trim());
      }
      if (formData.familyIncome && formData.familyIncome.trim() !== '') {
        familyData.familyIncome = formData.familyIncome.trim();
      }
      if (formData.livingWith && formData.livingWith.trim() !== '') {
        familyData.livingWith = formData.livingWith.trim().toLowerCase();
      }
      if (formData.familyBasedOutOf && formData.familyBasedOutOf.trim() !== '') {
        familyData.familyBasedOutOf = formData.familyBasedOutOf.trim();
      }

      if (Object.keys(familyData).length > 0) {
        profileData.family = familyData;
      }

      // Add Lifestyle fields (map to model's flat structure)
      if (formData.smokingHabits && formData.smokingHabits.trim() !== '') {
        profileData.smoking = denormalizeSmokingHabits(formData.smokingHabits.trim());
      }
      if (formData.drinkingHabits && formData.drinkingHabits.trim() !== '') {
        profileData.drinking = denormalizeDrinkingHabits(formData.drinkingHabits.trim());
      }
      if (formData.foodPreference && formData.foodPreference.trim() !== '') {
        profileData.diet = denormalizeFoodPreference(formData.foodPreference.trim());
      }
      if (formData.hobbies && formData.hobbies.trim() !== '') {
        // Convert comma-separated string to array
        profileData.hobbies = formData.hobbies.trim().split(',').map(h => h.trim()).filter(h => h !== '');
      }

      // Add Physical fields (map to model's flat structure)
      if (formData.bodyType && formData.bodyType.trim() !== '') {
        profileData.bodyType = formData.bodyType.trim().toLowerCase();
      }
      if (formData.complexion && formData.complexion.trim() !== '') {
        profileData.complexion = formData.complexion.trim().toLowerCase().replace(/ /g, '_');
      }
      if (formData.disability && formData.disability.trim() !== '') {
        profileData.disability = formData.disability.trim();
      }

      // Add new lifestyle fields
      if (formData.personalityTags && Array.isArray(formData.personalityTags) && formData.personalityTags.length > 0) {
        profileData.personalityTags = formData.personalityTags;
      }
      if (formData.thalassemia && formData.thalassemia.trim() !== '') {
        profileData.thalassemia = formData.thalassemia.trim().toLowerCase();
      }
      if (formData.hivPositive && formData.hivPositive.trim() !== '') {
        profileData.hivPositive = formData.hivPositive.trim().toLowerCase();
      }
      if (formData.horoscopeSign && formData.horoscopeSign.trim() !== '') {
        profileData.horoscopeSign = formData.horoscopeSign.trim().toLowerCase();
      }
      if (formData.ownHouse && formData.ownHouse.trim() !== '') {
        profileData.ownHouse = formData.ownHouse.trim().toLowerCase();
      }
      if (formData.openToPets && formData.openToPets.trim() !== '') {
        profileData.openToPets = formData.openToPets.trim().toLowerCase();
      }
      if (formData.ownCar && formData.ownCar.trim() !== '') {
        profileData.ownCar = formData.ownCar.trim().toLowerCase();
      }
      if (formData.foodICook && formData.foodICook.trim() !== '') {
        profileData.foodICook = formData.foodICook.trim();
      }
      if (formData.favoriteBooksGenre && Array.isArray(formData.favoriteBooksGenre) && formData.favoriteBooksGenre.length > 0) {
        profileData.favoriteBooksGenre = formData.favoriteBooksGenre;
      }
      if (formData.favoriteMusicGenre && Array.isArray(formData.favoriteMusicGenre) && formData.favoriteMusicGenre.length > 0) {
        profileData.favoriteMusicGenre = formData.favoriteMusicGenre;
      }
      if (formData.dressStyle && formData.dressStyle.trim() !== '') {
        profileData.dressStyle = formData.dressStyle.trim().toLowerCase();
      }
      if (formData.sports && formData.sports.trim() !== '') {
        profileData.sports = formData.sports.trim();
      }
      if (formData.cuisine && formData.cuisine.trim() !== '') {
        profileData.cuisine = formData.cuisine.trim();
      }
      if (formData.movies && formData.movies.trim() !== '') {
        profileData.movies = formData.movies.trim();
      }
      if (formData.tvShows && formData.tvShows.trim() !== '') {
        profileData.tvShows = formData.tvShows.trim();
      }
      if (formData.vacationDestination && formData.vacationDestination.trim() !== '') {
        profileData.vacationDestination = formData.vacationDestination.trim();
      }

      // Add Astrology fields (map to model's horoscope structure)
      const horoscopeData: any = {};
      if (formData.gotra && formData.gotra.trim() !== '') {
        profileData.gothra = formData.gotra.trim(); // Note: model uses 'gothra' not 'gotra'
      }
      if (formData.manglik && formData.manglik.trim() !== '') {
        horoscopeData.manglik = formData.manglik.trim().toLowerCase().replace(/ /g, '_');
      }

      if (Object.keys(horoscopeData).length > 0) {
        profileData.horoscope = horoscopeData;
      }

      // Add Partner Preferences
      const partnerPreferencesData: any = {};

      // Age Range - Ensure valid numbers
      const ageMin = parseInt(String(formData.partnerAgeMin));
      const ageMax = parseInt(String(formData.partnerAgeMax));

      if (!isNaN(ageMin)) {
        partnerPreferencesData.ageRange = partnerPreferencesData.ageRange || {};
        partnerPreferencesData.ageRange.min = ageMin;
      }
      if (!isNaN(ageMax)) {
        partnerPreferencesData.ageRange = partnerPreferencesData.ageRange || {};
        partnerPreferencesData.ageRange.max = ageMax;
      }

      // Height Range - Convert to CM and ensure valid numbers
      if (formData.partnerHeightMin && String(formData.partnerHeightMin).trim() !== '') {
        const heightStr = String(formData.partnerHeightMin).trim();
        const heightMatch = heightStr.match(/^(\d{1,2})'(\d{1,2})"$/);
        if (heightMatch) {
          const feet = parseInt(heightMatch[1]);
          const inches = parseInt(heightMatch[2]);
          const totalInches = feet * 12 + inches;
          partnerPreferencesData.heightRange = partnerPreferencesData.heightRange || {};
          partnerPreferencesData.heightRange.min = Math.round(totalInches * 2.54); // Convert to cm
        } else {
          const numericHeight = parseInt(heightStr);
          if (!isNaN(numericHeight)) {
            partnerPreferencesData.heightRange = partnerPreferencesData.heightRange || {};
            partnerPreferencesData.heightRange.min = numericHeight;
          }
        }
      }

      if (formData.partnerHeightMax && String(formData.partnerHeightMax).trim() !== '') {
        const heightStr = String(formData.partnerHeightMax).trim();
        const heightMatch = heightStr.match(/^(\d{1,2})'(\d{1,2})"$/);
        if (heightMatch) {
          const feet = parseInt(heightMatch[1]);
          const inches = parseInt(heightMatch[2]);
          const totalInches = feet * 12 + inches;
          partnerPreferencesData.heightRange = partnerPreferencesData.heightRange || {};
          partnerPreferencesData.heightRange.max = Math.round(totalInches * 2.54); // Convert to cm
        } else {
          const numericHeight = parseInt(heightStr);
          if (!isNaN(numericHeight)) {
            partnerPreferencesData.heightRange = partnerPreferencesData.heightRange || {};
            partnerPreferencesData.heightRange.max = numericHeight;
          }
        }
      }
      if (formData.partnerMaritalStatus && String(formData.partnerMaritalStatus).trim() !== '') {
        partnerPreferencesData.maritalStatus = String(formData.partnerMaritalStatus).trim();
      }
      if (formData.partnerReligion && String(formData.partnerReligion).trim() !== '') {
        partnerPreferencesData.religion = String(formData.partnerReligion).trim();
      }
      if (formData.partnerDiet && String(formData.partnerDiet).trim() !== '') {
        partnerPreferencesData.diet = denormalizeFoodPreference(String(formData.partnerDiet).trim());
      }
      if (formData.partnerSmoking && String(formData.partnerSmoking).trim() !== '') {
        partnerPreferencesData.smoking = denormalizeSmokingHabits(String(formData.partnerSmoking).trim());
      }
      if (formData.partnerDrinking && String(formData.partnerDrinking).trim() !== '') {
        partnerPreferencesData.drinking = denormalizeDrinkingHabits(String(formData.partnerDrinking).trim());
      }

      if (Object.keys(partnerPreferencesData).length > 0) {
        profileData.partnerPreferences = partnerPreferencesData;
      }

      if (primaryPhotoUrl) {
        profileData.profilePhoto = primaryPhotoUrl;
      }

      logger.log('📤 Saving profile data (denormalized for database):', profileData);

      // Step 4: Save profile via Redux
      const result = await dispatch(updateProfile(profileData)).unwrap();

      logger.log('✅ Profile saved successfully');

      // Check if changes are pending approval
      if (result.hasPendingChanges) {
        toast.info('Profile sent for verification!', {
          duration: 5000,
          description: 'Your profile will be updated once approved by admin.',
          className: 'bg-amber-50 border-amber-200'
        });
        setHasPendingChanges(true);
      } else {
        toast.success('Profile sent for verification!', {
          duration: 3000,
          className: 'bg-green-50 border-green-200'
        });
      }

      // Reset initial load flag so data reloads on next mount
      setInitialLoadDone(false);

      // Refresh profile data from backend
      await dispatch(fetchProfile());

      // Navigate back after a short delay
      setTimeout(() => {
        onBack();
      }, 1500);

    } catch (error: any) {
      logger.error('❌ Save error:', error);
      toast.error(error.message || 'Failed to update profile. Please try again.', {
        duration: 5000
      });
    } finally {
      setUploading(false);
      setIsSubmitting(false);
    }
  };

  // Show loading state while fetching profile
  if (loading && !initialLoadDone) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <Loader2 className="size-12 animate-spin text-rose-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  const activePhotos = photos.filter(p => !p.markedForDeletion);
  const primaryPhoto = activePhotos.find(p => p.isPrimary);
  const hasPhotos = activePhotos.length > 0;
  const isSaving = isSubmitting || saving || uploading;

  return (
    <div className="h-screen flex flex-col bg-white overflow-hidden">
      {/* Header */}
      <header className="bg-gradient-to-r from-rose-600 to-amber-500 text-white px-4 py-3 flex items-center justify-between flex-shrink-0 z-40 shadow-md">
        <Button
          variant="ghost"
          size="icon"
          className="text-white hover:bg-white/20"
          onClick={onBack}
          disabled={isSaving}
        >
          <ArrowLeft className="size-6" />
        </Button>
        <div className="flex flex-col items-center">
          <span className="text-lg">Edit Profile</span>
          <span className="text-xs text-white/80">Fields marked with * are required</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-white hover:bg-white/20"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <span className="flex items-center gap-2">
              <Loader2 className="size-4 animate-spin" />
              Saving
            </span>
          ) : (
            'Save'
          )}
        </Button>
      </header>

      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6 pb-24 md:pb-6">

          {/* Pending Changes Banner */}
          {hasPendingChanges && (
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4 animate-in fade-in slide-in-from-top-2 duration-300">
              <div className="flex items-start gap-3">
                <Clock className="size-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-yellow-900 mb-1">Profile Changes Pending Approval</p>
                  <p className="text-sm text-yellow-800">
                    Your recent profile changes are currently under review by our admin team.
                    Once approved, your updates will be visible to other users. You can continue to make additional changes.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Summary */}
          {Object.keys(errors).length > 0 && Object.keys(touched).length > 0 && (
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 animate-in fade-in slide-in-from-top-2 duration-300">
              <div className="flex items-start gap-3">
                <AlertCircle className="size-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-800 font-semibold mb-2">Please fix the following errors:</p>
                  <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                    {Object.entries(errors).map(([field, error]) => (
                      <li key={field}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Photo Gallery Section */}
          <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <Label className="text-base flex items-center gap-2">
                <ImageIcon className="size-5 text-rose-500" />
                Profile Photos ({activePhotos.length}/6)
              </Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={activePhotos.length >= 6 || isSaving}
              >
                <Upload className="size-4 mr-2" />
                Add Photos
              </Button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleImageSelect}
            />

            {/* Photo Grid */}
            {hasPhotos ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {activePhotos.map((photo, index) => (
                  <div key={photo.id} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200">
                    <ImageWithFallback
                      src={photo.url}
                      alt={`Profile photo ${index + 1}`}
                      className="w-full h-full object-cover"
                    />

                    {/* Approval Status Badges */}
                    {photo.status === 'pending' && !photo.isNew && (
                      <div className="absolute top-2 left-2 bg-yellow-500/90 text-white text-[10px] uppercase font-bold px-2 py-0.5 rounded shadow-sm">
                        Pending
                      </div>
                    )}
                    {photo.status === 'approved' && !photo.isNew && (
                      <div className="absolute top-2 left-2 bg-green-500/80 text-white px-1.5 py-0.5 rounded-full shadow-sm">
                        <Check className="size-3" />
                      </div>
                    )}
                    {photo.status === 'rejected' && !photo.isNew && (
                      <div className="absolute top-2 left-2 bg-red-500/90 text-white text-[10px] uppercase font-bold px-2 py-0.5 rounded shadow-sm">
                        Rejected
                      </div>
                    )}
                    {photo.isNew && (
                      <div className="absolute top-2 left-2 bg-blue-500/90 text-white text-[10px] uppercase font-bold px-2 py-0.5 rounded shadow-sm">
                        New
                      </div>
                    )}

                    {/* Primary Badge */}
                    {photo.isPrimary && (
                      <div className="absolute top-2 right-2 bg-rose-500 text-white p-1 rounded-full shadow-sm z-10">
                        <Star className="size-3 fill-current" />
                      </div>
                    )}

                    {/* Hover Actions */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      {!photo.isPrimary && (
                        <button
                          onClick={() => handleSetPrimary(photo.id)}
                          className="p-2 bg-white/20 hover:bg-white/40 text-white rounded-full backdrop-blur-sm transition-colors"
                          title="Set as profile picture"
                        >
                          <Star className="size-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleRemovePhoto(photo.id)}
                        className="p-2 bg-white/20 hover:bg-red-500/80 text-white rounded-full backdrop-blur-sm transition-colors"
                        title="Delete photo"
                      >
                        <X className="size-4" />
                      </button>
                    </div>
                  </div>
                ))}

                {/* Add Photo Button (if less than 6 photos) */}
                {activePhotos.length < 6 && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:text-rose-500 hover:border-rose-300 hover:bg-rose-50 transition-colors"
                    disabled={isSaving}
                  >
                    <Camera className="size-8 mb-2" />
                    <span className="text-sm font-medium">Add Photo</span>
                  </button>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                <UserAvatar
                  src={null}
                  firstName={user?.firstName}
                  lastName={user?.lastName}
                  size="xl"
                  className="mb-4"
                />
                <p className="text-gray-600 mb-2">No photos added yet</p>
                <p className="text-sm text-gray-500 mb-4">Add up to 6 photos</p>
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSaving}
                >
                  <Upload className="size-4 mr-2" />
                  Upload Photos
                </Button>
              </div>
            )}

            <p className="text-xs text-gray-500 mt-4">
              💡 Tip: Add multiple photos to increase profile visibility. First photo is shown as profile picture.
            </p>
          </div>

          {/* Basic Information */}
          <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User className="size-5 text-rose-500" />
              Basic Information
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Gender Field */}
                {isFieldVisible('gender') && (
                  <div>
                    <Label htmlFor="gender">
                      Gender {isFieldRequired('gender') && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      id="gender"
                      value={formData.gender}
                      disabled={true}
                      className="bg-gray-100 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">Gender cannot be changed</p>
                  </div>
                )}

                {/* Date of Birth Field */}
                {isFieldVisible('dateOfBirth') && (
                  <div>
                    <Label htmlFor="dateOfBirth">
                      Date of Birth {isFieldRequired('dateOfBirth') && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      id="dateOfBirth"
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={(e) => handleFieldChange('dateOfBirth', e.target.value)}
                      onBlur={() => handleBlur('dateOfBirth')}
                      className={touched.dateOfBirth && errors.dateOfBirth ? 'border-red-500' : ''}
                      disabled={isSaving}
                    />
                    {touched.dateOfBirth && errors.dateOfBirth && (
                      <p className="text-xs text-red-500 mt-1">{errors.dateOfBirth}</p>
                    )}
                  </div>
                )}

                {/* Height Field */}
                {isFieldVisible('height') && (
                  <div>
                    <Label htmlFor="height">
                      Height {isFieldRequired('height') && <span className="text-red-500">*</span>}
                    </Label>
                    <Select
                      value={formData.height}
                      onValueChange={(value) => {
                        handleFieldChange('height', value);
                      }}
                      disabled={isSaving}
                    >
                      <SelectTrigger className={touched.height && errors.height ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select height" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 36 }, (_, i) => {
                          const totalInches = 48 + i;
                          const feet = Math.floor(totalInches / 12);
                          const inches = totalInches % 12;
                          return (
                            <SelectItem key={i} value={`${feet}'${inches}"`}>
                              {feet}&apos;{inches}&quot;
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {touched.height && errors.height && (
                      <p className="text-xs text-red-500 mt-1">{errors.height}</p>
                    )}
                  </div>
                )}

                {/* Weight Field */}
                {isFieldVisible('weight') && (
                  <div>
                    <Label htmlFor="weight">
                      Weight (kg) {isFieldRequired('weight') && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      id="weight"
                      type="number"
                      placeholder="e.g., 65"
                      value={formData.weight}
                      onChange={(e) => handleFieldChange('weight', e.target.value)}
                      onBlur={() => handleBlur('weight')}
                      className={touched.weight && errors.weight ? 'border-red-500' : ''}
                      disabled={isSaving}
                    />
                    {touched.weight && errors.weight && (
                      <p className="text-xs text-red-500 mt-1">{errors.weight}</p>
                    )}
                  </div>
                )}

                {/* Marital Status Field */}
                {isFieldVisible('maritalStatus') && (
                  <div>
                    <Label htmlFor="maritalStatus">
                      Marital Status {isFieldRequired('maritalStatus') && <span className="text-red-500">*</span>}
                    </Label>
                    <div className="mt-2">
                      <SelectablePills
                        options={[
                          { label: 'Never Married', value: 'Never Married' },
                          { label: 'Divorced', value: 'Divorced' },
                          { label: 'Widowed', value: 'Widowed' },
                          { label: 'Separated', value: 'Separated' }
                        ]}
                        value={formData.maritalStatus}
                        onChange={(value) => {
                          handleFieldChange('maritalStatus', value as string);
                          handleBlur('maritalStatus');
                        }}
                      />
                    </div>
                    {touched.maritalStatus && errors.maritalStatus && (
                      <p className="text-xs text-red-500 mt-1">{errors.maritalStatus}</p>
                    )}
                  </div>
                )}
              </div>

              {/* About Me Field */}
              {isFieldVisible('about') && (
                <div>
                  <Label htmlFor="about">
                    About Me {isFieldRequired('about') && <span className="text-red-500">*</span>}
                  </Label>
                  <Textarea
                    id="about"
                    placeholder="Tell us about yourself..."
                    value={formData.about}
                    onChange={(e) => handleFieldChange('about', e.target.value)}
                    rows={4}
                    disabled={isSaving}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Religious Background */}
          {(isFieldVisible('religion') || isFieldVisible('community') || isFieldVisible('caste') || isFieldVisible('motherTongue')) && (
            <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Heart className="size-5 text-rose-500" />
                Religious Background
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Religion Field */}
                  {isFieldVisible('religion') && (
                    <div>
                      <Label htmlFor="religion">
                        Religion {isFieldRequired('religion') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="mt-2">
                        <SelectablePills
                          options={[
                            { label: 'Hindu', value: 'Hindu' },
                            { label: 'Muslim', value: 'Muslim' },
                            { label: 'Christian', value: 'Christian' },
                            { label: 'Sikh', value: 'Sikh' },
                            { label: 'Buddhist', value: 'Buddhist' },
                            { label: 'Jain', value: 'Jain' },
                            { label: 'Other', value: 'Other' }
                          ]}
                          value={formData.religion}
                          onChange={(value) => {
                            handleFieldChange('religion', value as string);
                            handleBlur('religion');
                          }}
                        />
                      </div>
                      {touched.religion && errors.religion && (
                        <p className="text-xs text-red-500 mt-1">{errors.religion}</p>
                      )}
                    </div>
                  )}

                  {/* Community Field */}
                  {isFieldVisible('community') && (
                    <div>
                      <Label htmlFor="community">
                        Community {isFieldRequired('community') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="mt-2">
                        <SelectablePills
                          options={[
                            { label: 'Brahmin', value: 'Brahmin' },
                            { label: 'Kshatriya', value: 'Kshatriya' },
                            { label: 'Vaishya', value: 'Vaishya' },
                            { label: 'Shudra', value: 'Shudra' },
                            { label: 'Scheduled Caste', value: 'Scheduled Caste' },
                            { label: 'Scheduled Tribe', value: 'Scheduled Tribe' },
                            { label: 'OBC', value: 'OBC' },
                            { label: 'Sikh', value: 'Sikh' },
                            { label: 'Muslim', value: 'Muslim' },
                            { label: 'Christian', value: 'Christian' },
                            { label: 'Jain', value: 'Jain' },
                            { label: 'Buddhist', value: 'Buddhist' },
                            { label: 'Parsi', value: 'Parsi' },
                            { label: 'Other', value: 'Other' }
                          ]}
                          value={formData.community}
                          onChange={(value) => {
                            handleFieldChange('community', value as string);
                            setFormData(prev => ({ ...prev, community: value as string, subCommunity: '' }));
                          }}
                        />
                      </div>
                      {touched.community && errors.community && (
                        <p className="text-xs text-red-500 mt-1">{errors.community}</p>
                      )}
                    </div>
                  )}

                  {/* Sub Community Field */}
                  {isFieldVisible('subCommunity') && (
                    <div>
                      <Label htmlFor="subCommunity">
                        Sub Community {isFieldRequired('subCommunity') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="mt-2">
                        {formData.community && communitySubCommunityMap[formData.community] ? (
                          <SelectablePills
                            options={communitySubCommunityMap[formData.community].map((subComm) => ({
                              label: subComm,
                              value: subComm
                            }))}
                            value={formData.subCommunity}
                            onChange={(value) => handleFieldChange('subCommunity', value as string)}
                          />
                        ) : (
                          <div className="text-sm text-gray-500 italic">Select a community first to see sub-communities</div>
                        )}
                      </div>
                      {touched.subCommunity && errors.subCommunity && (
                        <p className="text-xs text-red-500 mt-1">{errors.subCommunity}</p>
                      )}
                    </div>
                  )}

                  {/* Mother Tongue Field */}
                  {isFieldVisible('motherTongue') && (
                    <div>
                      <Label htmlFor="motherTongue">
                        Mother Tongue {isFieldRequired('motherTongue') && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id="motherTongue"
                        placeholder="e.g., Hindi, Tamil"
                        value={formData.motherTongue}
                        onChange={(e) => handleFieldChange('motherTongue', e.target.value)}
                        onBlur={() => handleBlur('motherTongue')}
                        className={touched.motherTongue && errors.motherTongue ? 'border-red-500' : ''}
                        disabled={isSaving}
                      />
                      {touched.motherTongue && errors.motherTongue && (
                        <p className="text-xs text-red-500 mt-1">{errors.motherTongue}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Location */}
          <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <MapPin className="size-5 text-rose-500" />
              Location
            </h3>
            <div className="space-y-4">
              <LocationSelector
                country={formData.country}
                state={formData.state}
                city={formData.city}
                onCountryChange={(value) => {
                  handleFieldChange('country', value);
                  handleBlur('country');
                }}
                onStateChange={(value) => {
                  handleFieldChange('state', value);
                  handleBlur('state');
                }}
                onCityChange={(value) => {
                  handleFieldChange('city', value);
                  handleBlur('city');
                }}
                errors={{
                  country: touched.country && errors.country ? errors.country : '',
                  state: touched.state && errors.state ? errors.state : '',
                  city: touched.city && errors.city ? errors.city : ''
                }}
                disabled={isSaving}
              />
            </div>
          </div>

          {/* Education */}
          {(isFieldVisible('educationLevel') || isFieldVisible('educationField') || isFieldVisible('educationInstitution')) && (
            <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <GraduationCap className="size-5 text-rose-500" />
                Education
              </h3>
              <div className="space-y-4">
                {/* Highest Qualification Field */}
                {isFieldVisible('educationLevel') && (
                  <div>
                    <Label htmlFor="educationLevel">
                      Highest Qualification {isFieldRequired('educationLevel') && <span className="text-red-500">*</span>}
                    </Label>
                    <div className="mt-2">
                      <SelectablePills
                        options={[
                          { label: 'High School', value: 'High School' },
                          { label: 'Diploma', value: 'Diploma' },
                          { label: "Bachelor's Degree", value: "Bachelor's" },
                          { label: "Master's Degree", value: "Master's" },
                          { label: 'PhD', value: 'PhD' }
                        ]}
                        value={formData.educationLevel}
                        onChange={(value) => {
                          handleFieldChange('educationLevel', value as string);
                          handleBlur('educationLevel');
                        }}
                      />
                    </div>
                    {touched.educationLevel && errors.educationLevel && (
                      <p className="text-xs text-red-500 mt-1">{errors.educationLevel}</p>
                    )}
                  </div>
                )}

                {/* Field of Study */}
                {isFieldVisible('educationField') && (
                  <div>
                    <Label htmlFor="educationField">
                      Field of Study {isFieldRequired('educationField') && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      id="educationField"
                      placeholder="e.g., Computer Science, Medicine"
                      value={formData.educationField}
                      onChange={(e) => handleFieldChange('educationField', e.target.value)}
                      onBlur={() => handleBlur('educationField')}
                      className={touched.educationField && errors.educationField ? 'border-red-500' : ''}
                      disabled={isSaving}
                    />
                    {touched.educationField && errors.educationField && (
                      <p className="text-xs text-red-500 mt-1">{errors.educationField}</p>
                    )}
                  </div>
                )}

                {/* Institution */}
                {isFieldVisible('educationInstitution') && (
                  <div>
                    <Label htmlFor="educationInstitution">
                      Institution {isFieldRequired('educationInstitution') && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      id="educationInstitution"
                      placeholder="e.g., University of Mumbai"
                      value={formData.educationInstitution}
                      onChange={(e) => handleFieldChange('educationInstitution', e.target.value)}
                      onBlur={() => handleBlur('educationInstitution')}
                      className={touched.educationInstitution && errors.educationInstitution ? 'border-red-500' : ''}
                      disabled={isSaving}
                    />
                    {touched.educationInstitution && errors.educationInstitution && (
                      <p className="text-xs text-red-500 mt-1">{errors.educationInstitution}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Career */}
          {(isFieldVisible('occupation') || isFieldVisible('employmentType') || isFieldVisible('workLocation') || isFieldVisible('annualIncome')) && (
            <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Briefcase className="size-5 text-rose-500" />
                Career
              </h3>
              <div className="space-y-4">
                {/* Occupation */}
                {isFieldVisible('occupation') && (
                  <div>
                    <Label htmlFor="occupation">
                      Occupation {isFieldRequired('occupation') && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      id="occupation"
                      placeholder="e.g., Software Engineer, Doctor"
                      value={formData.occupation}
                      onChange={(e) => handleFieldChange('occupation', e.target.value)}
                      onBlur={() => handleBlur('occupation')}
                      className={touched.occupation && errors.occupation ? 'border-red-500' : ''}
                      disabled={isSaving}
                    />
                    {touched.occupation && errors.occupation && (
                      <p className="text-xs text-red-500 mt-1">{errors.occupation}</p>
                    )}
                  </div>
                )}

                {/* Employment Type */}
                {isFieldVisible('employmentType') && (
                  <div>
                    <Label htmlFor="employmentType">
                      Employment Type {isFieldRequired('employmentType') && <span className="text-red-500">*</span>}
                    </Label>
                    <div className="mt-2">
                      <SelectablePills
                        options={[
                          { label: 'Full-Time', value: 'Full-Time' },
                          { label: 'Part-Time', value: 'Part-Time' },
                          { label: 'Self-Employed', value: 'Self-Employed' },
                          { label: 'Business', value: 'Business' },
                          { label: 'Freelance', value: 'Freelance' },
                          { label: 'Contract', value: 'Contract' },
                          { label: 'Unemployed', value: 'Unemployed' }
                        ]}
                        value={formData.employmentType}
                        onChange={(value) => {
                          handleFieldChange('employmentType', value as string);
                          handleBlur('employmentType');
                        }}
                      />
                    </div>
                    {touched.employmentType && errors.employmentType && (
                      <p className="text-xs text-red-500 mt-1">{errors.employmentType}</p>
                    )}
                  </div>
                )}

                {/* Work Location */}
                {isFieldVisible('workLocation') && (
                  <div>
                    <Label htmlFor="workLocation">
                      Work Location {isFieldRequired('workLocation') && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      id="workLocation"
                      placeholder="e.g., Mumbai, India"
                      value={formData.workLocation}
                      onChange={(e) => handleFieldChange('workLocation', e.target.value)}
                      onBlur={() => handleBlur('workLocation')}
                      className={touched.workLocation && errors.workLocation ? 'border-red-500' : ''}
                      disabled={isSaving}
                    />
                    {touched.workLocation && errors.workLocation && (
                      <p className="text-xs text-red-500 mt-1">{errors.workLocation}</p>
                    )}
                  </div>
                )}

                {/* Annual Income */}
                {isFieldVisible('annualIncome') && (
                  <div>
                    <Label htmlFor="annualIncome">
                      Annual Income {isFieldRequired('annualIncome') && <span className="text-red-500">*</span>}
                    </Label>
                    <div className="mt-2">
                      <SelectablePills
                        options={[
                          { label: '₹0 - 3 Lakh', value: '0-3' },
                          { label: '₹3 - 5 Lakh', value: '3-5' },
                          { label: '₹5 - 7 Lakh', value: '5-7' },
                          { label: '₹7 - 10 Lakh', value: '7-10' },
                          { label: '₹10 - 15 Lakh', value: '10-15' },
                          { label: '₹15 - 20 Lakh', value: '15-20' },
                          { label: '₹20 - 30 Lakh', value: '20-30' },
                          { label: '₹30 - 50 Lakh', value: '30-50' },
                          { label: '₹50+ Lakh', value: '50+' }
                        ]}
                        value={formData.annualIncome}
                        onChange={(value) => {
                          handleFieldChange('annualIncome', value as string);
                          handleBlur('annualIncome');
                        }}
                      />
                    </div>
                    {touched.annualIncome && errors.annualIncome && (
                      <p className="text-xs text-red-500 mt-1">{errors.annualIncome}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contact */}
          {(isFieldVisible('alternatePhone') || isFieldVisible('whatsappNumber')) && (
            <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Briefcase className="size-5 text-rose-500" />
                Contact Information
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Alternate Phone */}
                  {isFieldVisible('alternatePhone') && (
                    <div>
                      <Label htmlFor="alternatePhone">
                        Alternate Phone {isFieldRequired('alternatePhone') && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id="alternatePhone"
                        type="tel"
                        placeholder="e.g., +91 9876543210"
                        value={formData.alternatePhone}
                        onChange={(e) => handleFieldChange('alternatePhone', e.target.value)}
                        onBlur={() => handleBlur('alternatePhone')}
                        className={touched.alternatePhone && errors.alternatePhone ? 'border-red-500' : ''}
                        disabled={isSaving}
                      />
                      {touched.alternatePhone && errors.alternatePhone && (
                        <p className="text-xs text-red-500 mt-1">{errors.alternatePhone}</p>
                      )}
                    </div>
                  )}

                  {/* WhatsApp Number */}
                  {isFieldVisible('whatsappNumber') && (
                    <div>
                      <Label htmlFor="whatsappNumber">
                        WhatsApp Number {isFieldRequired('whatsappNumber') && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id="whatsappNumber"
                        type="tel"
                        placeholder="e.g., +91 9876543210"
                        value={formData.whatsappNumber}
                        onChange={(e) => handleFieldChange('whatsappNumber', e.target.value)}
                        onBlur={() => handleBlur('whatsappNumber')}
                        className={touched.whatsappNumber && errors.whatsappNumber ? 'border-red-500' : ''}
                        disabled={isSaving}
                      />
                      {touched.whatsappNumber && errors.whatsappNumber && (
                        <p className="text-xs text-red-500 mt-1">{errors.whatsappNumber}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Family */}
          {(isFieldVisible('fatherName') || isFieldVisible('fatherOccupation') || isFieldVisible('motherName') || isFieldVisible('motherOccupation') || isFieldVisible('brothers') || isFieldVisible('sisters') || isFieldVisible('familyType') || isFieldVisible('familyStatus') || isFieldVisible('familyValues')) && (
            <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Heart className="size-5 text-rose-500" />
                Family Background
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Father's Name */}
                  {isFieldVisible('fatherName') && (
                    <div>
                      <Label htmlFor="fatherName">
                        Father&apos;s Name {isFieldRequired('fatherName') && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id="fatherName"
                        placeholder="Enter father's name"
                        value={formData.fatherName}
                        onChange={(e) => handleFieldChange('fatherName', e.target.value)}
                        onBlur={() => handleBlur('fatherName')}
                        className={touched.fatherName && errors.fatherName ? 'border-red-500' : ''}
                        disabled={isSaving}
                      />
                      {touched.fatherName && errors.fatherName && (
                        <p className="text-xs text-red-500 mt-1">{errors.fatherName}</p>
                      )}
                    </div>
                  )}

                  {/* Father's Occupation */}
                  {isFieldVisible('fatherOccupation') && (
                    <div>
                      <Label htmlFor="fatherOccupation">
                        Father&apos;s Occupation {isFieldRequired('fatherOccupation') && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id="fatherOccupation"
                        placeholder="Enter father's occupation"
                        value={formData.fatherOccupation}
                        onChange={(e) => handleFieldChange('fatherOccupation', e.target.value)}
                        onBlur={() => handleBlur('fatherOccupation')}
                        className={touched.fatherOccupation && errors.fatherOccupation ? 'border-red-500' : ''}
                        disabled={isSaving}
                      />
                      {touched.fatherOccupation && errors.fatherOccupation && (
                        <p className="text-xs text-red-500 mt-1">{errors.fatherOccupation}</p>
                      )}
                    </div>
                  )}

                  {/* Mother's Name */}
                  {isFieldVisible('motherName') && (
                    <div>
                      <Label htmlFor="motherName">
                        Mother&apos;s Name {isFieldRequired('motherName') && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id="motherName"
                        placeholder="Enter mother's name"
                        value={formData.motherName}
                        onChange={(e) => handleFieldChange('motherName', e.target.value)}
                        onBlur={() => handleBlur('motherName')}
                        className={touched.motherName && errors.motherName ? 'border-red-500' : ''}
                        disabled={isSaving}
                      />
                      {touched.motherName && errors.motherName && (
                        <p className="text-xs text-red-500 mt-1">{errors.motherName}</p>
                      )}
                    </div>
                  )}

                  {/* Mother's Occupation */}
                  {isFieldVisible('motherOccupation') && (
                    <div>
                      <Label htmlFor="motherOccupation">
                        Mother&apos;s Occupation {isFieldRequired('motherOccupation') && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id="motherOccupation"
                        placeholder="Enter mother's occupation"
                        value={formData.motherOccupation}
                        onChange={(e) => handleFieldChange('motherOccupation', e.target.value)}
                        onBlur={() => handleBlur('motherOccupation')}
                        className={touched.motherOccupation && errors.motherOccupation ? 'border-red-500' : ''}
                        disabled={isSaving}
                      />
                      {touched.motherOccupation && errors.motherOccupation && (
                        <p className="text-xs text-red-500 mt-1">{errors.motherOccupation}</p>
                      )}
                    </div>
                  )}

                  {/* Number of Brothers */}
                  {isFieldVisible('brothers') && (
                    <div>
                      <Label htmlFor="brothers">
                        Number of Brothers {isFieldRequired('brothers') && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id="brothers"
                        type="number"
                        placeholder="0"
                        min="0"
                        value={formData.brothers}
                        onChange={(e) => handleFieldChange('brothers', e.target.value)}
                        onBlur={() => handleBlur('brothers')}
                        className={touched.brothers && errors.brothers ? 'border-red-500' : ''}
                        disabled={isSaving}
                      />
                      {touched.brothers && errors.brothers && (
                        <p className="text-xs text-red-500 mt-1">{errors.brothers}</p>
                      )}
                    </div>
                  )}

                  {/* Number of Sisters */}
                  {isFieldVisible('sisters') && (
                    <div>
                      <Label htmlFor="sisters">
                        Number of Sisters {isFieldRequired('sisters') && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id="sisters"
                        type="number"
                        placeholder="0"
                        min="0"
                        value={formData.sisters}
                        onChange={(e) => handleFieldChange('sisters', e.target.value)}
                        onBlur={() => handleBlur('sisters')}
                        className={touched.sisters && errors.sisters ? 'border-red-500' : ''}
                        disabled={isSaving}
                      />
                      {touched.sisters && errors.sisters && (
                        <p className="text-xs text-red-500 mt-1">{errors.sisters}</p>
                      )}
                    </div>
                  )}

                  {/* Family Type */}
                  {isFieldVisible('familyType') && (
                    <div>
                      <Label htmlFor="familyType">
                        Family Type {isFieldRequired('familyType') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="mt-2">
                        <SelectablePills
                          options={[
                            { label: 'Nuclear Family', value: 'Nuclear' },
                            { label: 'Joint Family', value: 'Joint' }
                          ]}
                          value={formData.familyType}
                          onChange={(value) => {
                            handleFieldChange('familyType', value as string);
                            handleBlur('familyType');
                          }}
                        />
                      </div>
                      {touched.familyType && errors.familyType && (
                        <p className="text-xs text-red-500 mt-1">{errors.familyType}</p>
                      )}
                    </div>
                  )}

                  {/* Family Status */}
                  {isFieldVisible('familyStatus') && (
                    <div>
                      <Label htmlFor="familyStatus">
                        Family Status {isFieldRequired('familyStatus') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="mt-2">
                        <SelectablePills
                          options={[
                            { label: 'Middle Class', value: 'Middle Class' },
                            { label: 'Upper Middle Class', value: 'Upper Middle Class' },
                            { label: 'Rich', value: 'Rich' },
                            { label: 'Affluent', value: 'Affluent' }
                          ]}
                          value={formData.familyStatus}
                          onChange={(value) => {
                            handleFieldChange('familyStatus', value as string);
                            handleBlur('familyStatus');
                          }}
                        />
                      </div>
                      {touched.familyStatus && errors.familyStatus && (
                        <p className="text-xs text-red-500 mt-1">{errors.familyStatus}</p>
                      )}
                    </div>
                  )}

                  {/* Family Values */}
                  {isFieldVisible('familyValues') && (
                    <div>
                      <Label htmlFor="familyValues">
                        Family Values {isFieldRequired('familyValues') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="mt-2">
                        <SelectablePills
                          options={[
                            { label: 'Traditional', value: 'Traditional' },
                            { label: 'Moderate', value: 'Moderate' },
                            { label: 'Liberal', value: 'Liberal' }
                          ]}
                          value={formData.familyValues}
                          onChange={(value) => {
                            handleFieldChange('familyValues', value as string);
                            handleBlur('familyValues');
                          }}
                        />
                      </div>
                      {touched.familyValues && errors.familyValues && (
                        <p className="text-xs text-red-500 mt-1">{errors.familyValues}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Lifestyle */}
          {(isFieldVisible('smokingHabits') || isFieldVisible('drinkingHabits') || isFieldVisible('foodPreference') || isFieldVisible('hobbies')) && (
            <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Heart className="size-5 text-rose-500" />
                Lifestyle
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Smoking Habits */}
                  {isFieldVisible('smokingHabits') && (
                    <div>
                      <Label htmlFor="smokingHabits">
                        Smoking Habits {isFieldRequired('smokingHabits') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="mt-2">
                        <SelectablePills
                          options={[
                            { label: 'Non-Smoker', value: 'Non-Smoker' },
                            { label: 'Occasional Smoker', value: 'Occasional Smoker' },
                            { label: 'Regular Smoker', value: 'Regular Smoker' }
                          ]}
                          value={formData.smokingHabits}
                          onChange={(value) => {
                            handleFieldChange('smokingHabits', value as string);
                            handleBlur('smokingHabits');
                          }}
                        />
                      </div>
                      {touched.smokingHabits && errors.smokingHabits && (
                        <p className="text-xs text-red-500 mt-1">{errors.smokingHabits}</p>
                      )}
                    </div>
                  )}

                  {/* Drinking Habits */}
                  {isFieldVisible('drinkingHabits') && (
                    <div>
                      <Label htmlFor="drinkingHabits">
                        Drinking Habits {isFieldRequired('drinkingHabits') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="mt-2">
                        <SelectablePills
                          options={[
                            { label: 'Non-Drinker', value: 'Non-Drinker' },
                            { label: 'Social Drinker', value: 'Social Drinker' },
                            { label: 'Regular Drinker', value: 'Regular Drinker' }
                          ]}
                          value={formData.drinkingHabits}
                          onChange={(value) => {
                            handleFieldChange('drinkingHabits', value as string);
                            handleBlur('drinkingHabits');
                          }}
                        />
                      </div>
                      {touched.drinkingHabits && errors.drinkingHabits && (
                        <p className="text-xs text-red-500 mt-1">{errors.drinkingHabits}</p>
                      )}
                    </div>
                  )}

                  {/* Food Preference */}
                  {isFieldVisible('foodPreference') && (
                    <div>
                      <Label htmlFor="foodPreference">
                        Food Preference {isFieldRequired('foodPreference') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="mt-2">
                        <SelectablePills
                          options={[
                            { label: 'Vegetarian', value: 'Vegetarian' },
                            { label: 'Non-Vegetarian', value: 'Non-Vegetarian' },
                            { label: 'Eggetarian', value: 'Eggetarian' },
                            { label: 'Vegan', value: 'Vegan' }
                          ]}
                          value={formData.foodPreference}
                          onChange={(value) => {
                            handleFieldChange('foodPreference', value as string);
                            handleBlur('foodPreference');
                          }}
                        />
                      </div>
                      {touched.foodPreference && errors.foodPreference && (
                        <p className="text-xs text-red-500 mt-1">{errors.foodPreference}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Hobbies */}
                {isFieldVisible('hobbies') && (
                  <div>
                    <Label htmlFor="hobbies">
                      Hobbies {isFieldRequired('hobbies') && <span className="text-red-500">*</span>}
                    </Label>
                    <Textarea
                      id="hobbies"
                      placeholder="Tell us about your hobbies and interests..."
                      value={formData.hobbies}
                      onChange={(e) => handleFieldChange('hobbies', e.target.value)}
                      onBlur={() => handleBlur('hobbies')}
                      className={touched.hobbies && errors.hobbies ? 'border-red-500' : ''}
                      rows={3}
                      disabled={isSaving}
                    />
                    {touched.hobbies && errors.hobbies && (
                      <p className="text-xs text-red-500 mt-1">{errors.hobbies}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Physical */}
          {(isFieldVisible('bodyType') || isFieldVisible('complexion') || isFieldVisible('disability')) && (
            <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <User className="size-5 text-rose-500" />
                Physical Attributes
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Body Type */}
                  {isFieldVisible('bodyType') && (
                    <div>
                      <Label htmlFor="bodyType">
                        Body Type {isFieldRequired('bodyType') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="mt-2">
                        <SelectablePills
                          options={[
                            { label: 'Slim', value: 'Slim' },
                            { label: 'Average', value: 'Average' },
                            { label: 'Athletic', value: 'Athletic' },
                            { label: 'Heavy', value: 'Heavy' }
                          ]}
                          value={formData.bodyType}
                          onChange={(value) => {
                            handleFieldChange('bodyType', value as string);
                            handleBlur('bodyType');
                          }}
                        />
                      </div>
                      {touched.bodyType && errors.bodyType && (
                        <p className="text-xs text-red-500 mt-1">{errors.bodyType}</p>
                      )}
                    </div>
                  )}

                  {/* Complexion */}
                  {isFieldVisible('complexion') && (
                    <div>
                      <Label htmlFor="complexion">
                        Complexion {isFieldRequired('complexion') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="mt-2">
                        <SelectablePills
                          options={[
                            { label: 'Fair', value: 'Fair' },
                            { label: 'Wheatish', value: 'Wheatish' },
                            { label: 'Dark', value: 'Dark' }
                          ]}
                          value={formData.complexion}
                          onChange={(value) => {
                            handleFieldChange('complexion', value as string);
                            handleBlur('complexion');
                          }}
                        />
                      </div>
                      {touched.complexion && errors.complexion && (
                        <p className="text-xs text-red-500 mt-1">{errors.complexion}</p>
                      )}
                    </div>
                  )}

                  {/* Physical Disability */}
                  {isFieldVisible('disability') && (
                    <div>
                      <Label htmlFor="disability">
                        Physical Disability {isFieldRequired('disability') && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id="disability"
                        placeholder="None or specify"
                        value={formData.disability}
                        onChange={(e) => handleFieldChange('disability', e.target.value)}
                        onBlur={() => handleBlur('disability')}
                        className={touched.disability && errors.disability ? 'border-red-500' : ''}
                        disabled={isSaving}
                      />
                      {touched.disability && errors.disability && (
                        <p className="text-xs text-red-500 mt-1">{errors.disability}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Astrology */}
          {(isFieldVisible('gotra') || isFieldVisible('manglik')) && (
            <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Star className="size-5 text-rose-500" />
                Astrology
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Gotra */}
                  {isFieldVisible('gotra') && (
                    <div>
                      <Label htmlFor="gotra">
                        Gotra {isFieldRequired('gotra') && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id="gotra"
                        placeholder="Enter gotra"
                        value={formData.gotra}
                        onChange={(e) => handleFieldChange('gotra', e.target.value)}
                        onBlur={() => handleBlur('gotra')}
                        className={touched.gotra && errors.gotra ? 'border-red-500' : ''}
                        disabled={isSaving}
                      />
                      {touched.gotra && errors.gotra && (
                        <p className="text-xs text-red-500 mt-1">{errors.gotra}</p>
                      )}
                    </div>
                  )}

                  {/* Manglik Status */}
                  {isFieldVisible('manglik') && (
                    <div>
                      <Label htmlFor="manglik">
                        Manglik Status {isFieldRequired('manglik') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="mt-2">
                        <SelectablePills
                          options={[
                            { label: 'Yes', value: 'Yes' },
                            { label: 'No', value: 'No' },
                            { label: 'Anshik (Partial)', value: 'Anshik' },
                            { label: "Don't Know", value: "Don't Know" }
                          ]}
                          value={formData.manglik}
                          onChange={(value) => {
                            handleFieldChange('manglik', value as string);
                            handleBlur('manglik');
                          }}
                        />
                      </div>
                      {touched.manglik && errors.manglik && (
                        <p className="text-xs text-red-500 mt-1">{errors.manglik}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Partner Preferences */}
          {(isFieldVisible('partnerPreferences.ageRange.min') || isFieldVisible('partnerPreferences.ageRange.max') || isFieldVisible('partnerPreferences.heightRange.min') || isFieldVisible('partnerPreferences.heightRange.max') || isFieldVisible('partnerPreferences.maritalStatus') || isFieldVisible('partnerPreferences.religion') || isFieldVisible('partnerPreferences.diet') || isFieldVisible('partnerPreferences.smoking') || isFieldVisible('partnerPreferences.drinking')) && (
            <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Heart className="size-5 text-rose-500" />
                Partner Preferences
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Partner Age Range */}
                  {isFieldVisible('partnerPreferences.ageRange.min') && (
                    <div>
                      <Label htmlFor="partnerAgeMin">
                        Partner Age From {isFieldRequired('partnerPreferences.ageRange.min') && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id="partnerAgeMin"
                        type="number"
                        placeholder="e.g., 25"
                        value={formData.partnerAgeMin}
                        onChange={(e) => handleFieldChange('partnerAgeMin', e.target.value)}
                        onBlur={() => handleBlur('partnerAgeMin')}
                        className={touched.partnerAgeMin && errors.partnerAgeMin ? 'border-red-500' : ''}
                        disabled={isSaving}
                      />
                      {touched.partnerAgeMin && errors.partnerAgeMin && (
                        <p className="text-xs text-red-500 mt-1">{errors.partnerAgeMin}</p>
                      )}
                    </div>
                  )}

                  {isFieldVisible('partnerPreferences.ageRange.max') && (
                    <div>
                      <Label htmlFor="partnerAgeMax">
                        Partner Age To {isFieldRequired('partnerPreferences.ageRange.max') && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id="partnerAgeMax"
                        type="number"
                        placeholder="e.g., 35"
                        value={formData.partnerAgeMax}
                        onChange={(e) => handleFieldChange('partnerAgeMax', e.target.value)}
                        onBlur={() => handleBlur('partnerAgeMax')}
                        className={touched.partnerAgeMax && errors.partnerAgeMax ? 'border-red-500' : ''}
                        disabled={isSaving}
                      />
                      {touched.partnerAgeMax && errors.partnerAgeMax && (
                        <p className="text-xs text-red-500 mt-1">{errors.partnerAgeMax}</p>
                      )}
                    </div>
                  )}

                  {/* Partner Height Range */}
                  {isFieldVisible('partnerPreferences.heightRange.min') && (
                    <div>
                      <Label htmlFor="partnerHeightMin">
                        Partner Height From {isFieldRequired('partnerPreferences.heightRange.min') && <span className="text-red-500">*</span>}
                      </Label>
                      <Select
                        value={formData.partnerHeightMin}
                        onValueChange={(value) => {
                          handleFieldChange('partnerHeightMin', value);
                        }}
                        disabled={isSaving}
                      >
                        <SelectTrigger className={touched.partnerHeightMin && errors.partnerHeightMin ? 'border-red-500' : ''}>
                          <SelectValue placeholder="Select height" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 36 }, (_, i) => {
                            const totalInches = 48 + i;
                            const feet = Math.floor(totalInches / 12);
                            const inches = totalInches % 12;
                            return (
                              <SelectItem key={i} value={`${feet}'${inches}"`}>
                                {feet}&apos;{inches}&quot;
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      {touched.partnerHeightMin && errors.partnerHeightMin && (
                        <p className="text-xs text-red-500 mt-1">{errors.partnerHeightMin}</p>
                      )}
                    </div>
                  )}

                  {isFieldVisible('partnerPreferences.heightRange.max') && (
                    <div>
                      <Label htmlFor="partnerHeightMax">
                        Partner Height To {isFieldRequired('partnerPreferences.heightRange.max') && <span className="text-red-500">*</span>}
                      </Label>
                      <Select
                        value={formData.partnerHeightMax}
                        onValueChange={(value) => {
                          handleFieldChange('partnerHeightMax', value);
                        }}
                        disabled={isSaving}
                      >
                        <SelectTrigger className={touched.partnerHeightMax && errors.partnerHeightMax ? 'border-red-500' : ''}>
                          <SelectValue placeholder="Select height" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 36 }, (_, i) => {
                            const totalInches = 48 + i;
                            const feet = Math.floor(totalInches / 12);
                            const inches = totalInches % 12;
                            return (
                              <SelectItem key={i} value={`${feet}'${inches}"`}>
                                {feet}&apos;{inches}&quot;
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      {touched.partnerHeightMax && errors.partnerHeightMax && (
                        <p className="text-xs text-red-500 mt-1">{errors.partnerHeightMax}</p>
                      )}
                    </div>
                  )}

                  {/* Partner Marital Status */}
                  {isFieldVisible('partnerPreferences.maritalStatus') && (
                    <div>
                      <Label htmlFor="partnerMaritalStatus">
                        Partner Marital Status {isFieldRequired('partnerPreferences.maritalStatus') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="mt-2">
                        <SelectablePills
                          options={[
                            { label: 'Never Married', value: 'Never Married' },
                            { label: 'Divorced', value: 'Divorced' },
                            { label: 'Widowed', value: 'Widowed' },
                            { label: 'Separated', value: 'Separated' },
                            { label: 'Any', value: 'Any' }
                          ]}
                          value={formData.partnerMaritalStatus}
                          onChange={(value) => {
                            handleFieldChange('partnerMaritalStatus', value as string);
                            handleBlur('partnerMaritalStatus');
                          }}
                        />
                      </div>
                      {touched.partnerMaritalStatus && errors.partnerMaritalStatus && (
                        <p className="text-xs text-red-500 mt-1">{errors.partnerMaritalStatus}</p>
                      )}
                    </div>
                  )}

                  {/* Partner Religion */}
                  {isFieldVisible('partnerPreferences.religion') && (
                    <div>
                      <Label htmlFor="partnerReligion">
                        Partner Religion {isFieldRequired('partnerPreferences.religion') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="mt-2">
                        <SelectablePills
                          options={[
                            { label: 'Hindu', value: 'Hindu' },
                            { label: 'Muslim', value: 'Muslim' },
                            { label: 'Christian', value: 'Christian' },
                            { label: 'Sikh', value: 'Sikh' },
                            { label: 'Jain', value: 'Jain' },
                            { label: 'Buddhist', value: 'Buddhist' },
                            { label: 'Any', value: 'Any' }
                          ]}
                          value={formData.partnerReligion}
                          onChange={(value) => {
                            handleFieldChange('partnerReligion', value as string);
                            handleBlur('partnerReligion');
                          }}
                        />
                      </div>
                      {touched.partnerReligion && errors.partnerReligion && (
                        <p className="text-xs text-red-500 mt-1">{errors.partnerReligion}</p>
                      )}
                    </div>
                  )}

                  {/* Partner Diet */}
                  {isFieldVisible('partnerPreferences.diet') && (
                    <div>
                      <Label htmlFor="partnerDiet">
                        Partner Food Preference {isFieldRequired('partnerPreferences.diet') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="mt-2">
                        <SelectablePills
                          options={[
                            { label: 'Vegetarian', value: 'Vegetarian' },
                            { label: 'Non-Vegetarian', value: 'Non-Vegetarian' },
                            { label: 'Eggetarian', value: 'Eggetarian' },
                            { label: 'Vegan', value: 'Vegan' },
                            { label: 'Any', value: 'Any' }
                          ]}
                          value={formData.partnerDiet}
                          onChange={(value) => {
                            handleFieldChange('partnerDiet', value as string);
                            handleBlur('partnerDiet');
                          }}
                        />
                      </div>
                      {touched.partnerDiet && errors.partnerDiet && (
                        <p className="text-xs text-red-500 mt-1">{errors.partnerDiet}</p>
                      )}
                    </div>
                  )}

                  {/* Partner Smoking */}
                  {isFieldVisible('partnerPreferences.smoking') && (
                    <div>
                      <Label htmlFor="partnerSmoking">
                        Partner Smoking Habits {isFieldRequired('partnerPreferences.smoking') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="mt-2">
                        <SelectablePills
                          options={[
                            { label: 'Non-Smoker', value: 'Non-Smoker' },
                            { label: 'Occasional Smoker', value: 'Occasional Smoker' },
                            { label: 'Regular Smoker', value: 'Regular Smoker' },
                            { label: 'Any', value: 'Any' }
                          ]}
                          value={formData.partnerSmoking}
                          onChange={(value) => {
                            handleFieldChange('partnerSmoking', value as string);
                            handleBlur('partnerSmoking');
                          }}
                        />
                      </div>
                      {touched.partnerSmoking && errors.partnerSmoking && (
                        <p className="text-xs text-red-500 mt-1">{errors.partnerSmoking}</p>
                      )}
                    </div>
                  )}

                  {/* Partner Drinking */}
                  {isFieldVisible('partnerPreferences.drinking') && (
                    <div>
                      <Label htmlFor="partnerDrinking">
                        Partner Drinking Habits {isFieldRequired('partnerPreferences.drinking') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="mt-2">
                        <SelectablePills
                          options={[
                            { label: 'Non-Drinker', value: 'Non-Drinker' },
                            { label: 'Social Drinker', value: 'Social Drinker' },
                            { label: 'Regular Drinker', value: 'Regular Drinker' },
                            { label: 'Any', value: 'Any' }
                          ]}
                          value={formData.partnerDrinking}
                          onChange={(value) => {
                            handleFieldChange('partnerDrinking', value as string);
                            handleBlur('partnerDrinking');
                          }}
                        />
                      </div>
                      {touched.partnerDrinking && errors.partnerDrinking && (
                        <p className="text-xs text-red-500 mt-1">{errors.partnerDrinking}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Extended Profile Fields */}
          <ExtendedProfileFields
            formData={formData}
            onChange={(field, value) => {
              setFormData(prev => ({ ...prev, [field]: value }));
              setTouched(prev => ({ ...prev, [field]: true }));
            }}
            errors={errors}
            touched={touched}
          />

          {/* Save Button - Fixed at bottom on mobile */}
          <div className="md:hidden fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 shadow-lg">
            <Button
              className="w-full bg-gradient-to-r from-rose-600 to-amber-500 hover:from-rose-700 hover:to-amber-600 text-white"
              size="lg"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="size-5 animate-spin" />
                  Saving Changes...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Check className="size-5" />
                  Save Profile
                </span>
              )}
            </Button>
          </div>

          {/* Desktop Save Button */}
          <div className="hidden md:block">
            <Button
              className="w-full bg-gradient-to-r from-rose-600 to-amber-500 hover:from-rose-700 hover:to-amber-600 text-white"
              size="lg"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="size-5 animate-spin" />
                  Saving Changes...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Check className="size-5" />
                  Save Profile
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}